/**
 * LunarCrush Research content model (DEV-11563).
 *
 * The permanent home for LunarCrush Research studies lives at /research.
 * Each study is fully content/config-driven: a `Study` object describes the
 * hero, an ordered list of section `blocks`, the downloadable data assets, the
 * "Talk to Joe" CTA and the per-study SEO/OG + JSON-LD schema. `ResearchStudyPage`
 * renders any study by walking `blocks` against a fixed component registry, so
 * adding study #2 is a data edit here — no new page-level code.
 *
 * Study #1 is a 1:1 port of the Nico-approved standalone page
 * (https://lc-earnings-atlas.vercel.app). Numbers below are transcribed verbatim
 * from that page's embedded dataset — nothing is invented.
 */

/* ─── Shared value types ─────────────────────────────────────────── */

/** A hero metric card with an animated count-up value (or a static text value). */
export interface StudyStat {
  label: string;
  /** Target number the card counts up to. Omit when `text` is set. */
  countUp?: number;
  /** Static text value (e.g. "Tim Cook") rendered without animation. */
  text?: string;
  /** Decimal places to render (0 = integer with thousands separators). */
  decimals?: number;
  prefix?: string;
  suffix?: string;
  note: string;
}

/** A downloadable data asset. Served from /research/<file> in public/. */
export interface StudyDownload {
  label: string;
  /** File name under public/research/. */
  file: string;
  format: 'parquet' | 'csv' | 'md' | 'pdf';
  /** schema.org DataDownload encodingFormat. */
  encodingFormat: string;
  /** Renders as the solid primary button (vs. ghost). */
  primary?: boolean;
}

/** How a table cell is formatted, driven by column config (reusable per study). */
export type CellKind =
  | 'index'      // 1-based row number, muted
  | 'symbol'     // ticker logo + link to the LunarCrush topic page
  | 'text'       // plain text (styled via `tone`)
  | 'date'       // plain date string
  | 'zscore'     // e.g. "5.2σ"
  | 'move'       // signed percentage, green/red
  | 'sentiment'  // e.g. "80/100"
  | 'num'        // integer with thousands separators
  | 'gap';       // pre-signed string like "+78 pts" / "-57 pts", green/red by sign

export interface TableColumn {
  /** Key into each row object, or `__index` for the running row number. */
  key: string;
  label: string;
  kind: CellKind;
  /** For zscore and text cells: accent (blue), muted, or pos/neg (green/red). */
  tone?: 'accent' | 'muted' | 'pos' | 'neg';
}

export interface TableSpec {
  /** Optional heading rendered above the table (used by the blind-spot pair). */
  heading?: string;
  columns: TableColumn[];
  rows: Record<string, string | number>[];
}

/** One timestamped step in a reconstruction timeline (Binance Hack study). */
export interface StudyStep {
  /** Display time, e.g. "20:24:23" or "Oct 28 07:39". */
  time: string;
  title: string;
  body: string;
  /** Who first described the step in public, and when. */
  seen?: string;
}

/* Interactive multi-lane timeline (TimelineGantt). Times are ISO-8601 UTC. */
export type GanttTone = 'blue' | 'red' | 'green' | 'amber' | 'muted' | 'violet';
export interface GanttLane {
  key: string;
  label: string;
  tone: GanttTone;
}
/** A slice of the time axis; several slices make a broken axis (weight = share of width). */
export interface GanttSegment {
  from: string;
  to: string;
  weight: number;
  label: string;
}
export interface GanttEvent {
  t: string;
  /** Present for spans (e.g. a rehearsal window). */
  end?: string;
  lane: string;
  /** Display time for the detail card, e.g. "Oct 29 20:24:23". */
  time: string;
  title: string;
  detail?: string;
  seen?: string;
  /** Short always-visible label; omit to show the event on hover only. */
  short?: string;
  labelPos?: 'above' | 'below';
}
/** A shaded window, across every lane or inside one lane. */
export interface GanttBand {
  from: string;
  to: string;
  lane?: string;
  label?: string;
  tone: GanttTone;
}
export interface GanttNote {
  lane: string;
  from: string;
  to: string;
  text: string;
}
export interface GanttTick {
  t: string;
  label: string;
}

export interface BarRow {
  label: string;
  value: number;
}

/* ─── Section blocks ─────────────────────────────────────────────── */

interface BlockBase {
  /** Numbered eyebrow, e.g. "01 — THE SHAPE OF ATTENTION". */
  kicker: string;
  title: string;
  sub?: string;
}

/** Points for the animated attention curve (day offset, z-score). */
export interface AttentionCurvePoint {
  day: number;
  z: number;
}

/** One verbatim LLM answer pair for the qa block (Context Advantage). */
export interface QaPair {
  offTag: string;
  offText: string;
  onTag: string;
  onText: string;
}

/** One series in a line chart (DEV-12818). */
export interface LineSeries {
  label: string;
  values: (number | null)[];
  color: string;
  fill?: boolean;
  type?: 'bar';
  /** Bar fill for negative values (e.g. a sentiment gap below zero). */
  negColor?: string;
  axis?: 'right';
}

export type StudyBlock = BlockBase &
  (
    | { kind: 'attention-curve'; curve: AttentionCurvePoint[]; peakDay: number; peakLabel: string }
    | { kind: 'table'; table: TableSpec }
    | { kind: 'two-tables'; left: TableSpec; right: TableSpec }
    | { kind: 'bars'; unit: string; bars: BarRow[]; decimals?: number }
    | { kind: 'callout'; heading: string; paragraphs: string[] }
    | { kind: 'dataset'; downloads: StudyDownload[]; downloadNote: string; methodology?: string }
    // Hourly storm anatomy (Attention Cascade): raw interactions/hour series
    // rendered as a static SVG curve with the peak circled, plus the
    // first-mover table below it.
    | { kind: 'storm'; series: number[]; table: TableSpec }
    // Side-by-side verbatim LLM answers, with vs. without context (Context Advantage).
    | { kind: 'qa'; qa: QaPair }
    // Kicker + title + sub only. `subHtml` (trusted, authored in this file)
    // replaces `sub` when the copy carries a link.
    | { kind: 'prose'; subHtml?: string }
    // Multi-series line chart (DEV-12818 rates study). Lightweight SVG, no chart library.
    | { kind: 'lines'; labels: string[]; series: LineSeries[]; unit: string; scale?: 'log' }
    // A static chart image served from public/research/ (Binance Hack study).
    | { kind: 'figure'; src: string; alt: string; caption?: string }
    // Vertical timeline of timestamped steps with a "first public" column (Binance Hack study).
    | { kind: 'steps'; steps: StudyStep[]; tone?: 'blue' | 'red' | 'green' | 'amber' }
    // Interactive multi-lane timeline with a playhead sweep and hover detail (Binance Hack study).
    | {
        kind: 'gantt';
        ariaLabel: string;
        lanes: GanttLane[];
        segments: GanttSegment[];
        events: GanttEvent[];
        bands?: GanttBand[];
        notes?: GanttNote[];
        ticks?: GanttTick[];
        caption?: string;
      }
  );

/* ─── Study + SEO ────────────────────────────────────────────────── */

export interface StudyHero {
  badge: string;
  headline: string;
  /** Optional second line rendered in the Butler serif accent. */
  headlineAccent?: string;
  sub: string;
  stats: StudyStat[];
}

export interface TalkToJoe {
  heading: string;
  body: string;
  name: string;
  role: string;
  email: string;
  /** mailto subject line. */
  subject: string;
  headshot: string;
}

/** Dataset JSON-LD (schema.org/Dataset) content. */
export interface DatasetSchema {
  name: string;
  description: string;
  temporalCoverage?: string;
  variableMeasured?: string[];
}

export interface Study {
  slug: string;
  /** Card + hero title. */
  title: string;
  /** One-line hook for the index card. */
  hook: string;
  /** Human display date, e.g. "July 2026". */
  displayDate: string;
  /** ISO publish date for schema + sitemap lastmod. */
  datePublished: string;
  dateModified: string;
  /** Accent color for the index card top border. */
  accent: string;
  /** Per-study OG/social image served from public/. */
  ogImage: string;

  /** SEO meta. */
  metaTitle: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  twitterDescription: string;

  hero: StudyHero;
  blocks: StudyBlock[];
  /** Omit to render the study without the Talk-to-Joe CTA. */
  talkToJoe?: TalkToJoe;
  /** Omit for studies with no downloadable dataset (Article JSON-LD only). */
  dataset?: DatasetSchema;
}

const CANONICAL_BASE = 'https://lunarcrush.com';

/* ─── Study #1: The Earnings Social Atlas ────────────────────────── */

const earningsSocialAtlas: Study = {
  slug: 'earnings-social-atlas',
  title: 'The Earnings Social Atlas',
  hook: 'What 30–50M posts/hour saw around 2,898 earnings prints, Jan–Jun 2026 — attention curves, league tables, blind spots, and the full dataset.',
  displayDate: 'July 2026',
  datePublished: '2026-07-15',
  dateModified: '2026-07-22',
  // DEV-11563: was a raw hex literal, identical to the value research.css
  // already defines as --rc-green. Raw hex is forbidden in design-system-
  // governed paths (scripts/check-raw-hex.mjs). The violation predates this
  // ticket's follow-up work; it only surfaced now because the guard scopes
  // itself to files changed in the push, and this file changed.
  accent: 'var(--rc-green)',
  ogImage: `${CANONICAL_BASE}/research/og-image.png`,

  metaTitle: 'The Earnings Social Atlas — LunarCrush Research',
  metaDescription:
    'What 30-50M posts/hour saw around 2,898 earnings prints: attention curves, league tables, blind spots, and the full downloadable event dataset.',
  ogTitle: 'The Earnings Social Atlas',
  ogDescription:
    'What 30-50M posts/hour saw around 2,898 earnings prints, Jan-Jun 2026. Attention curves, league tables, blind spots, and the full event dataset — free to download.',
  twitterDescription:
    'The crowd leans in 7 days before the print. 2,898 events, free dataset, no registration.',

  hero: {
    badge: 'NEW RESEARCH · JULY 2026 · FREE DATASET BELOW',
    headline: 'The Earnings',
    headlineAccent: 'Social Atlas.',
    sub: 'Q2 earnings season starts this week. Here is what 30–50M social posts per hour saw around the last 2,898 prints: when the crowd leans in, what it gets right, and where it is completely blind.',
    stats: [
      { label: 'Earnings events', countUp: 2898, note: 'Jan–Jun 2026, two full seasons' },
      { label: 'Equities tracked', countUp: 3048, note: 'of 7,000+ in full coverage' },
      { label: 'Print-day attention', countUp: 1.8, decimals: 1, prefix: '+', suffix: 'σ', note: "vs each name's own baseline" },
      { label: 'Prints going >2σ social', countUp: 30, suffix: '%', note: 'liquid names, print window' },
    ],
  },

  blocks: [
    {
      kind: 'attention-curve',
      kicker: '01 — THE SHAPE OF ATTENTION',
      title: 'The crowd leans in a week early',
      sub: "Average social attention (z-score vs each name's own 60-day baseline) across all 1,665 liquid-name prints. The build starts ~7 days out, doubles the day before, peaks at +1.8σ on the print, and takes a week to decay. This is daily data; the intraday lead-lag lives in our hourly feed.",
      peakDay: 0,
      peakLabel: 'PRINT DAY',
      curve: [
        { day: -10, z: 0.132 }, { day: -9, z: 0.212 }, { day: -8, z: 0.313 }, { day: -7, z: 0.39 },
        { day: -6, z: 0.473 }, { day: -5, z: 0.53 }, { day: -4, z: 0.631 }, { day: -3, z: 0.712 },
        { day: -2, z: 0.809 }, { day: -1, z: 1.155 }, { day: 0, z: 1.79 }, { day: 1, z: 1.413 },
        { day: 2, z: 0.885 }, { day: 3, z: 0.595 }, { day: 4, z: 0.505 }, { day: 5, z: 0.528 },
        { day: 6, z: 0.533 }, { day: 7, z: 0.526 },
      ],
    },
    {
      kind: 'table',
      kicker: '02 — THE LEAGUE TABLE',
      title: 'The loudest prints of the season',
      sub: "Mega-caps ranked by social reaction. Note what tops it: not the biggest moves, the biggest conversations. UNH's collapse hit 5.2σ. HPE moved +30% at 4.2σ.",
      table: {
        columns: [
          { key: '__index', label: '#', kind: 'index' },
          { key: 'symbol', label: 'Ticker', kind: 'symbol' },
          { key: 'name', label: 'Company', kind: 'text' },
          { key: 'report_date', label: 'Print date', kind: 'date' },
          { key: 'post_z', label: 'Social reaction', kind: 'zscore', tone: 'accent' },
          { key: 'ret_2d', label: '2-day move', kind: 'move' },
          { key: 'post_sent', label: 'Crowd sentiment', kind: 'sentiment' },
        ],
        rows: [
          { symbol: 'MDLZ', report_date: '2026-02-03', post_z: 7.727, ret_2d: 0.0121, post_sent: 96.0, name: 'Mondelez International' },
          { symbol: 'PANW', report_date: '2026-02-17', post_z: 5.381, ret_2d: -0.08745, post_sent: 75.0, name: 'Palo Alto Networks' },
          { symbol: 'UNH', report_date: '2026-01-27', post_z: 5.154, ret_2d: -0.16386, post_sent: 79.7, name: 'UnitedHealth Group' },
          { symbol: 'HSY', report_date: '2026-02-05', post_z: 5.012, ret_2d: 0.12508, post_sent: 75.0, name: 'The Hershey Company' },
          { symbol: 'SMTC', report_date: '2026-03-16', post_z: 4.814, ret_2d: -0.06647, post_sent: 76.0, name: 'Semtech Corporation' },
          { symbol: 'TSN', report_date: '2026-05-04', post_z: 4.759, ret_2d: 0.07475, post_sent: 77.0, name: 'Tyson Foods' },
          { symbol: 'TOL', report_date: '2026-02-17', post_z: 4.644, ret_2d: -0.03684, post_sent: 84.0, name: 'Toll Brothers' },
          { symbol: 'GRMN', report_date: '2026-02-18', post_z: 4.639, ret_2d: 0.10517, post_sent: 73.3, name: 'Garmin Ltd.' },
          { symbol: 'MKC', report_date: '2026-06-25', post_z: 4.489, ret_2d: 0.07248, post_sent: 78.7, name: 'McCormick & Company' },
          { symbol: 'CSL', report_date: '2026-02-03', post_z: 4.336, ret_2d: 0.15104, post_sent: 67.3, name: 'Carlisle Companies' },
          { symbol: 'DASH', report_date: '2026-05-06', post_z: 4.291, ret_2d: 0.07372, post_sent: 60.0, name: 'DoorDash' },
          { symbol: 'SPGI', report_date: '2026-02-10', post_z: 4.213, ret_2d: -0.12029, post_sent: 68.7, name: 'S&P Global' },
          { symbol: 'HPE', report_date: '2026-06-01', post_z: 4.193, ret_2d: 0.30367, post_sent: 82.3, name: 'Hewlett Packard Enterprise Company' },
          { symbol: 'KEYS', report_date: '2026-02-23', post_z: 4.141, ret_2d: -0.02007, post_sent: 78.0, name: 'Keysight Technologies' },
          { symbol: 'DVA', report_date: '2026-05-05', post_z: 4.141, ret_2d: 0.25856, post_sent: 67.3, name: 'DaVita' },
        ],
      },
    },
    {
      kind: 'two-tables',
      kicker: '03 — THE BLIND SPOTS',
      title: "Where the crowd wasn't looking",
      sub: 'Both edges are screens. Left: double-digit moves the crowd barely reacted to (under-covered names your competitors saw late). Right: multi-sigma conversation storms that priced almost nothing in.',
      left: {
        heading: '😴 Quiet shocks · big move, no crowd',
        columns: [
          { key: 'symbol', label: 'Ticker', kind: 'symbol' },
          { key: 'report_date', label: 'Date', kind: 'date' },
          { key: 'ret_2d', label: 'Move', kind: 'move' },
          { key: 'post_z', label: 'Social', kind: 'zscore', tone: 'muted' },
        ],
        rows: [
          { symbol: 'HRB', report_date: '2026-05-06', ret_2d: 0.20813, post_z: -4.116, name: 'H&R Block' },
          { symbol: 'FTDR', report_date: '2026-02-26', ret_2d: 0.21794, post_z: -2.437, name: 'Frontdoor' },
          { symbol: 'VZ', report_date: '2026-01-30', ret_2d: 0.12057, post_z: -0.907, name: 'Verizon Communications' },
          { symbol: 'OSK', report_date: '2026-05-08', ret_2d: -0.12355, post_z: -0.9, name: 'Oshkosh Corporation (Holding Compa' },
          { symbol: 'ATR', report_date: '2026-04-30', ret_2d: 0.13337, post_z: -0.865, name: 'AptarGroup' },
          { symbol: 'FLR', report_date: '2026-05-08', ret_2d: 0.12514, post_z: -0.823, name: 'Fluor Corporation' },
          { symbol: 'PEGA', report_date: '2026-04-21', ret_2d: -0.14429, post_z: -0.759, name: 'Pegasystems' },
          { symbol: 'IRDM', report_date: '2026-02-12', ret_2d: 0.25515, post_z: -0.671, name: 'Iridium Communications' },
        ],
      },
      right: {
        heading: '📣 All hype · big crowd, no move',
        columns: [
          { key: 'symbol', label: 'Ticker', kind: 'symbol' },
          { key: 'report_date', label: 'Date', kind: 'date' },
          { key: 'ret_2d', label: 'Move', kind: 'move' },
          { key: 'post_z', label: 'Social', kind: 'zscore', tone: 'accent' },
        ],
        rows: [
          { symbol: 'MDLZ', report_date: '2026-02-03', ret_2d: 0.0121, post_z: 7.727, name: 'Mondelez International' },
          { symbol: 'ENVA', report_date: '2026-01-27', ret_2d: 0.00448, post_z: 4.771, name: 'Enova International' },
          { symbol: 'KEYS', report_date: '2026-02-23', ret_2d: -0.02007, post_z: 4.141, name: 'Keysight Technologies' },
          { symbol: 'YUM', report_date: '2026-04-29', ret_2d: 0.02077, post_z: 4.011, name: 'Yum! Brands' },
          { symbol: 'DRI', report_date: '2026-03-19', ret_2d: 0.01166, post_z: 3.906, name: 'Darden Restaurants' },
          { symbol: 'AEP', report_date: '2026-05-05', ret_2d: -0.01574, post_z: 3.643, name: 'American Electric Power Company' },
          { symbol: 'VNOM', report_date: '2026-05-04', ret_2d: 0.0016, post_z: 3.605, name: 'Viper Energy' },
          { symbol: 'GNTX', report_date: '2026-01-30', ret_2d: -0.01165, post_z: 3.495, name: 'Gentex Corporation' },
        ],
      },
    },
    {
      kind: 'bars',
      kicker: '04 — SECTOR PROFILES',
      title: 'Who gets talked about on print day',
      sub: 'Median print-window social reaction by sector. Materials, tech and staples run hot; utilities report in silence.',
      unit: 'σ',
      bars: [
        { label: 'Basic Materials', value: 1.78 },
        { label: 'Technology', value: 1.67 },
        { label: 'Consumer Staples', value: 1.65 },
        { label: 'Consumer Discretionary', value: 1.46 },
        { label: 'Health Care', value: 1.34 },
        { label: 'Telecommunications', value: 1.25 },
        { label: 'Energy', value: 1.21 },
        { label: 'Finance', value: 1.13 },
        { label: 'Industrials', value: 1.09 },
        { label: 'Real Estate', value: 1.03 },
        { label: 'Utilities', value: 0.87 },
      ],
    },
    {
      kind: 'callout',
      kicker: '05 — WHERE THE SIGNAL ACTUALLY LIVES',
      title: '',
      heading: 'The daily number, straight up — and why it is the floor',
      paragraphs: [
        'We tested the obvious hypothesis ourselves: does pre-print social momentum predict print-day move size? At daily granularity, top-decile names moved ~1.2–1.4× more than bottom-decile. We publish that number because you would find it yourself in an hour with the file below — and because it is the signal measured the crudest possible way: daily bars, no timing, no sequencing, no constituent history. That is the floor, not the ceiling.',
        '29.7% of liquid prints build more than 1σ of attention before the number is public. The question that decides whether that is tradeable is sequencing: who moved the conversation first, at what hour, and whether attention led or chased price inside the day. Daily bars cannot answer it by construction. The hourly feed with point-in-time constituent history exists for exactly that question. Teams that license it are not betting that attention matters — they are measuring when it arrives, hours before the tape shows it.',
      ],
    },
    {
      kind: 'dataset',
      kicker: '06 — THE DATASET',
      title: "Don't take our word for it",
      sub: 'Every event in this study: symbol, report date, pre/post social z-scores, crowd sentiment, print move, 5-day drift, EPS surprise, sector. No registration.',
      downloads: [
        { label: '⬇ Parquet (2,898 events)', file: 'lc_earnings_social_events_2026H1.parquet', format: 'parquet', encodingFormat: 'application/vnd.apache.parquet', primary: true },
        { label: '⬇ CSV', file: 'lc_earnings_social_events_2026H1.csv', format: 'csv', encodingFormat: 'text/csv' },
        { label: '⬇ AI guide (.md for your LLM)', file: 'lc_earnings_social_events_2026H1.md', format: 'md', encodingFormat: 'text/markdown' },
      ],
      downloadNote:
        'The .md file is a companion brief for AI assistants: schema, methodology, validation anchors, and starter analyses. Drop it into Claude or ChatGPT next to the data and your model starts from the right place.',
      methodology:
        'Methodology: attention = log1p(daily interactions); z vs each name\'s own t−67…t−8 baseline (min 40 obs). Pre-window t−7…t−1, post-window t…t+2. Moves = close-to-close straddling the print (2-day, timing-agnostic), |move|>60% excluded. Earnings dates + surprises: Nasdaq calendar. Universe: 3,048 US equities continuously tracked by LunarCrush.',
    },
  ],

  talkToJoe: {
    heading: 'Talk to Joe',
    body: 'Two-week institutional trials include the full hourly feed, 5+ years of history, point-in-time constituent membership, and this exact panel cut to your tickers. Most teams have it in their backtester the same week.',
    name: 'Joe Vezzani',
    role: 'Co-founder & CEO, LunarCrush',
    email: 'joe@lunarcrush.com',
    subject: 'Earnings Atlas trial',
    headshot: '/research/joe-headshot.png',
  },

  dataset: {
    name: 'LunarCrush Earnings Social Events 2026 H1',
    description:
      '2,898 US earnings prints (Jan-Jun 2026) with pre/post social attention z-scores, crowd sentiment, print-day move, 5-day drift, EPS surprise and sector. Attention measured across X, Reddit, TikTok, YouTube, Instagram and 10,000+ news sources.',
    temporalCoverage: '2026-01-01/2026-06-30',
    variableMeasured: ['social attention z-score', 'crowd sentiment', 'earnings print move', 'EPS surprise'],
  },
};

/* ─── Study #2: The Attention Cascade ────────────────────────────── */

const STORM_TABLE_COLUMNS = [
  { key: 'lead', label: 'Lead', kind: 'text', tone: 'accent' },
  { key: 'net', label: 'Network', kind: 'text' },
  { key: 'creator', label: 'Creator', kind: 'text' },
  { key: 'followers', label: 'Followers', kind: 'num' },
  { key: 'interactions', label: 'Interactions', kind: 'num' },
  { key: 'post', label: 'Post', kind: 'text' },
] satisfies TableColumn[];

/** Standard AI-guide download note shared by the July 2026 standalones. */
const AI_GUIDE_NOTE =
  'The .md file is a companion brief for AI assistants: schema, methodology, and validation anchors. Drop it into Claude or ChatGPT next to the data and your model starts from the right place.';

const attentionCascade: Study = {
  slug: 'attention-cascade',
  title: 'The Attention Cascade',
  hook: 'Four live attention storms, hour by hour: who posted first and how much warning the crowd gives you, from 1.4 hours to 5 days. Free case file.',
  displayDate: 'July 2026',
  datePublished: '2026-07-22',
  dateModified: '2026-07-22',
  accent: 'var(--rc-blue)',
  ogImage: `${CANONICAL_BASE}/research/attention-cascade/og-image.png`,

  metaTitle: 'The Attention Cascade — LunarCrush Research',
  metaDescription:
    'Four live attention storms, hour by hour: who posted first and how much warning the crowd gives you, from 1.4 hours to 5 days. Free case file.',
  ogTitle: 'The Attention Cascade',
  ogDescription:
    'Four live attention storms, hour by hour: who posted first and how much warning the crowd gives you, from 1.4 hours to 5 days. Free case file.',
  twitterDescription:
    'Security news: 1.4h warning. Memecoins: 3h. Communities: 16h. Earnings: 5 days. Four storms, hour by hour.',

  hero: {
    badge: 'NEW RESEARCH · JULY 2026 · FOUR LIVE STORMS · FREE CASE FILE BELOW',
    headline: 'The Attention Cascade.',
    sub: 'How much warning does the crowd give you? Four attention storms from this week, hour by hour: who posted first, how early, and how fast each kind of storm moves from first post to peak.',
    stats: [
      { label: 'Storms captured', countUp: 4, note: 'this week, live' },
      { label: 'Fastest cascade', countUp: 1.4, decimals: 1, suffix: 'h', note: 'security disclosure → peak' },
      { label: 'Slowest build', countUp: 5, prefix: '~', suffix: 'd', note: 'earnings anticipation' },
      { label: 'Biggest spike', countUp: 53, suffix: 'x', note: "vs the topic's median hour" },
    ],
  },

  blocks: [
    {
      kind: 'table',
      kicker: '01 — THE FINDING',
      title: 'Warning time depends on the storm',
      sub: 'Each domain has its own cascade speed. Security news gives you about an hour. Memecoins give you an afternoon. Communities telegraph for half a day. Earnings anticipation builds for the better part of a week. If your data arrives in daily bars, every one of these is already over.',
      table: {
        columns: [
          { key: 'type', label: 'Storm type', kind: 'text' },
          { key: 'case', label: "This week's case", kind: 'text' },
          { key: 'spike', label: 'Spike', kind: 'text', tone: 'accent' },
          { key: 'warning', label: 'Warning before peak', kind: 'text', tone: 'pos' },
        ],
        rows: [
          { type: 'Tech/Security', case: 'Sharepoint', spike: '21x', warning: '1.4 hours' },
          { type: 'Memecoin', case: 'Dogecoin', spike: '32x', warning: '3.3 hours' },
          { type: 'Crypto', case: 'Pi Network', spike: '53x', warning: '15.8 hours' },
          { type: 'Equity', case: 'Unitedhealth', spike: '26x', warning: '~5 days' },
        ],
      },
    },
    {
      kind: 'storm',
      kicker: '02 — A ZERO-DAY GOES PUBLIC',
      title: 'Sharepoint: 21x in one hour',
      sub: 'Peak 125,612 interactions/hour at Jul 21, 11:00 PT, against a 5,998 median hour. Below the curve: the highest-engagement posts that were already live BEFORE the peak, and how early.',
      series: [4176, 3234, 3561, 3663, 3694, 4206, 3718, 3419, 4286, 6469, 5287, 5558, 5354, 6531, 79245, 77025, 6099, 5471, 5862, 5188, 5837, 5765, 5309, 5257, 4322, 4579, 5062, 5966, 8869, 7243, 8247, 6125, 6312, 5887, 6243, 6267, 6735, 6822, 6440, 15850, 9427, 9287, 125612, 8108, 8899, 10061, 9976, 7759, 5918, 6462, 6621, 5809, 6043, 4744, 4502, 4133, 3515, 3421, 3541, 6098, 6031, 4446, 6375, 3546, 7158, 11080, 17852, 27527],
      table: {
        columns: STORM_TABLE_COLUMNS,
        rows: [
          { lead: '−1.4h', net: 'X', creator: 'Cyber Security News', followers: 68349, interactions: 4970, post: 'Alert ❗️A newly disclosed vulnerability, tracked as CVE-2026-50522, is rattling enterprise' },
        ],
      },
    },
    {
      kind: 'storm',
      kicker: '03 — A MEMECOIN CATCHES A BID',
      title: 'Dogecoin: 32x in one hour',
      sub: 'Peak 2,926,135 interactions/hour at Jul 21, 11:00 PT, against a 92,263 median hour. Below the curve: the highest-engagement posts that were already live BEFORE the peak, and how early.',
      series: [65580, 162167, 138093, 47432, 41322, 39836, 36179, 36137, 36952, 29415, 164764, 31966, 34380, 32408, 35664, 46315, 44734, 50394, 1264641, 43576, 56182, 49325, 49405, 52835, 41768, 42389, 34836, 79947, 42339, 41282, 49599, 55830, 46144, 328309, 58480, 45834, 51574, 57356, 52695, 51532, 71488, 66173, 78150, 2312504, 78507, 134845, 120954, 135600, 185510, 147852, 289403, 343709, 218372, 159301, 123921, 124711, 120582, 86035, 101398, 104687, 101743, 141605, 103619, 116466, 99510, 104255, 113701, 288156, 133930, 118431, 2926135, 64604, 61172, 52494, 62544, 54921, 34665, 44390, 32267, 31390, 30963, 20141, 15654, 16147, 21874, 22813, 29547, 27187, 23501, 22064, 44531, 245692, 52407, 18608, 8256, 1946],
      table: {
        columns: STORM_TABLE_COLUMNS,
        rows: [
          { lead: '−3.3h', net: 'X', creator: 'Paulo Vidal', followers: 73859, interactions: 3051, post: 'The new website wen releasing with the #DogeGo App, will also include all documentation, i' },
          { lead: '−2.9h', net: 'YouTube', creator: 'Ahmet Zd', followers: 34300, interactions: 1428, post: 'Trump Boğaya Yeşil Işık Yakmış Olabilir!' },
          { lead: '−2.8h', net: 'X', creator: 'Paulo Vidal', followers: 73859, interactions: 2560, post: 'I have alredy everything ready to push #DogeGo Much Faster Full Dogecoin node, but my home' },
          { lead: '−2.8h', net: 'X', creator: 'REDWRITER', followers: 7190, interactions: 1533, post: 'After being outbid 69 times, I ended up winning the bid on Coco Shibu! \nThank you @Fantoum' },
          { lead: '−2.8h', net: 'X', creator: 'Dogecoin Rise 🌎🚀🌕', followers: 416354, interactions: 2984, post: 'Retail has always had a dream.\nStocks had "buy the dip."\nCrypto had... Wen Lambo \nThe perf' },
          { lead: '−2.5h', net: 'TikTok', creator: 'kn1od3d5x', followers: 11614, interactions: 6623, post: 'Will Doge dividends become reality? 🤯 Check out the latest updates! #DogeDividend #Stimulu' },
        ],
      },
    },
    {
      kind: 'storm',
      kicker: '04 — A COMMUNITY LIGHTS UP',
      title: 'Pi Network: 53x in one hour',
      sub: 'Peak 1,021,814 interactions/hour at Jul 21, 08:00 PT, against a 19,404 median hour. Below the curve: the highest-engagement posts that were already live BEFORE the peak, and how early.',
      series: [19610, 73711, 89986, 69876, 19395, 19413, 22534, 22207, 19322, 19688, 19522, 18035, 20484, 23669, 22361, 21090, 19888, 21331, 18517, 19517, 17003, 19528, 17597, 18840, 17098, 196244, 17347, 19159, 21099, 25041, 22583, 22971, 23492, 22334, 27244, 27270, 23634, 29487, 33335, 1021814, 27864, 19339, 18940, 15622, 12995, 13879, 11797, 8732, 9595, 14187, 11697, 11258, 7830, 7283, 11074, 15485, 14016, 16022, 13536, 15041, 16882, 20969, 20242, 23890, 17044, 14835, 9722, 1865],
      table: {
        columns: STORM_TABLE_COLUMNS,
        rows: [
          { lead: '−15.8h', net: 'TikTok', creator: 'Pi Network', followers: 13587, interactions: 850, post: 'Do you remember your first day on Pi? #pinetwork' },
          { lead: '−15.5h', net: 'TikTok', creator: 'PI MAiNNET ( Đang đến )', followers: 12510, interactions: 2112, post: 'Pi Network . Tổng giá trị tài sản thế gioi là bao nhiêu ?#LIVEIncentiveProgram #MyLIVEJour' },
          { lead: '−14.0h', net: 'TikTok', creator: 'Man boy', followers: 14106, interactions: 480, post: 'Pi Network Thailand 🇹🇭  Opp Meeting  ณ บ้านลำหอก บ้านด๊อกเตอร์ณิชาภัทร หม้อทอง ตำบลหนองเหล' },
          { lead: '−13.9h', net: 'X', creator: 'Đổ Liều . Pi', followers: 5458, interactions: 5046, post: 'Khi nhìn một bức tranh lớn ... ta mới hiểu giá trị của một hệ sinh thái .  Đây không còn l' },
          { lead: '−13.2h', net: 'X', creator: 'Pi Town', followers: 5793, interactions: 17863, post: '🔥💥 PI NETWORK APPEARS ON THIS MAP 😯 WHAT DOES IT MEAN? 🤔\nA diagram just surfaced on RootDa' },
          { lead: '−13.0h', net: 'X', creator: 'amrOnChain', followers: 6039, interactions: 7207, post: '💬 PIONEERS — When RootData maps OSL’s 90+ stablecoin partners and puts Pi Network on the s' },
        ],
      },
    },
    {
      kind: 'storm',
      kicker: '05 — AN EARNINGS PRINT BUILDS',
      title: 'Unitedhealth: 26x in one hour',
      sub: 'Peak 500,586 interactions/hour at Jul 21, 10:00 PT, against a 19,204 median hour. Below the curve: the highest-engagement posts that were already live BEFORE the peak, and how early.',
      series: [19661, 22685, 87842, 63366, 32343, 33000, 25774, 13270, 11160, 7830, 9753, 7316, 7261, 7151, 7456, 12217, 7309, 9690, 11323, 15479, 15150, 22757, 10782, 9370, 9331, 7517, 11397, 12782, 7242, 8372, 9790, 9532, 7033, 6274, 5600, 4709, 6349, 5472, 6259, 6987, 8601, 13360, 20862, 12939, 17436, 20192, 13584, 13842, 13735, 18063, 22182, 13000, 9309, 12110, 11313, 9181, 7694, 6292, 4496, 7551, 5058, 5060, 17505, 6866, 420257, 496008, 459594, 350102, 498552, 500586, 380293, 313586, 6871, 7146, 8507, 8006, 13163, 18344, 24440, 27018, 10957, 9766, 9820, 7071, 6670, 6538, 11958, 11896, 10345, 8792, 11319, 10446, 12599, 3125, 2828, 323],
      table: {
        columns: STORM_TABLE_COLUMNS,
        rows: [
          { lead: '−122.9h', net: 'X', creator: 'Brett Builds Wealth🎩', followers: 118, interactions: 172, post: '$XLV Healthcare strong move today, lead by $UNH 🟢🟢🟢' },
          { lead: '−122.7h', net: 'TikTok', creator: 'Nikki | Rediscovering Me', followers: 1154, interactions: 2555, post: '#workfromhome #wfh #wfhlife #healthcarejobs #wfhjobs' },
          { lead: '−115.7h', net: 'X', creator: 'Sam Forrester', followers: 82, interactions: 1403, post: 'Long overdue, but $UNH earnings beat! This is a $700 stock. Don\'t bet against monopolies i' },
          { lead: '−101.0h', net: 'X', creator: 'The Earnings Correspondent', followers: 63793, interactions: 18455, post: 'This is how the stocks of the reporting companies performed yesterday:\n$UAL $JBHT $HOMB $G' },
          { lead: '−98.9h', net: 'X', creator: 'The Long Investor', followers: 271036, interactions: 73960, post: '$ASTS I first added at $24\n$ADUR I first added at $3.85\n$WYFI I started adding at $30\n$HIM' },
          { lead: '−91.5h', net: 'X', creator: 'Bernie Sanders', followers: 14942635, interactions: 425510, post: 'Corporate greed is UnitedHealth making a $5.48 billion profit last quarter (up 60% from la' },
        ],
      },
    },
    {
      kind: 'callout',
      kicker: '06 — READ THIS BEFORE QUOTING IT',
      title: '',
      heading: 'What four storms can and cannot tell you',
      paragraphs: [
        "This is an anatomy, not a backtest: four storms, described honestly, captured while they were still in the posts window. 'First movers' means the earliest posts among what became the storm's most engaged content. The general claim this page supports is narrow and checkable: attention storms have structure, the structure differs by domain, and none of it is visible at daily granularity. Measuring it systematically requires the hourly feed, which is the commercial product.",
      ],
    },
    {
      kind: 'dataset',
      kicker: '07 — THE CASE FILE',
      title: "Don't take our word for it",
      sub: 'Every first-mover post in all four storms: lead time, creator, network, followers, interactions. No registration.',
      downloads: [
        { label: '⬇ CSV (first movers, 4 storms)', file: 'attention-cascade/lc_attention_cascade_2026-07-22.csv', format: 'csv', encodingFormat: 'text/csv', primary: true },
        { label: '⬇ AI guide (.md for your LLM)', file: 'attention-cascade/lc_attention_cascade_2026-07-22.md', format: 'md', encodingFormat: 'text/markdown' },
      ],
      downloadNote: AI_GUIDE_NOTE,
    },
  ],

  talkToJoe: {
    heading: 'Talk to Joe',
    body: 'The hourly feed captures every storm like this, live, across every tracked asset and topic, with the creator-level detail that makes first movers findable while the storm is still forming.',
    name: 'Joe Vezzani',
    role: 'Co-founder & CEO, LunarCrush',
    email: 'joe@lunarcrush.com',
    subject: 'Attention Cascade',
    headshot: '/research/joe-headshot.png',
  },

  dataset: {
    name: 'LunarCrush Attention Cascade case file 2026-07-22',
    description: 'Hourly anatomy of four social attention storms with pre-peak first-mover posts.',
  },
};

/* ─── Study #3: The Context Advantage ────────────────────────────── */

const contextAdvantage: Study = {
  slug: 'context-advantage',
  title: 'The Context Advantage',
  hook: 'Same model, same market question, with and without LunarCrush context. Both answers printed verbatim. Packs downloadable.',
  displayDate: 'July 2026',
  datePublished: '2026-07-22',
  dateModified: '2026-07-22',
  accent: 'var(--rc-green-b)',
  ogImage: `${CANONICAL_BASE}/research/context-advantage/og-image.png`,

  metaTitle: 'The Context Advantage — LunarCrush Research',
  metaDescription:
    'Same model, same market question, with and without LunarCrush context. Both answers printed verbatim. Packs downloadable.',
  ogTitle: 'The Context Advantage',
  ogDescription:
    'Same model, same market question, with and without LunarCrush context. Both answers printed verbatim. Packs downloadable.',
  twitterDescription:
    'Ungrounded models answer market questions with confident fiction. Grounded ones cite the crowd. Verbatim, side by side.',

  hero: {
    badge: 'NEW RESEARCH · JULY 2026 · LIVE EXPERIMENT · PACKS DOWNLOADABLE BELOW',
    headline: 'The Context Advantage.',
    sub: 'What does your AI actually know about markets right now? We asked a frontier model three live market questions bare, then asked again with a LunarCrush context pack. Both answers are printed verbatim.',
    stats: [
      { label: 'Questions', countUp: 3, note: 'live market, same day' },
      { label: 'Runs', countUp: 6, note: 'with and without context' },
      { label: 'Packs', countUp: 3, note: 'downloadable, timestamped' },
      { label: 'Setup time', countUp: 1, prefix: '~', suffix: 'min', note: 'paste pack, ask again' },
    ],
  },

  blocks: [
    {
      kind: 'prose',
      kicker: '01 — THE EXPERIMENT',
      title: 'Same model, same question, one variable',
      sub: 'Each question was asked twice on the same day: once bare, once with a small markdown context pack built from live LunarCrush data (interactions, engagement sentiment, per-network mood, attention spikes, timestamped posts). The model was explicitly told to say so when it could not know current data. Answers below are verbatim and unedited.',
    },
    {
      kind: 'qa',
      kicker: '02 — QUESTION 1',
      title: 'What is the social-media crowd saying about unitedhealth stock right now, and why?',
      sub: 'Same model, same question, asked 2026-07-22. Left: the model alone. Right: the model with a LunarCrush context pack (downloadable below).',
      qa: {
        offTag: 'WITHOUT LIVE CONTEXT',
        offText:
          'Currently, social media sentiment surrounding UnitedHealth (UNH) stock is highly bullish, with retail tracking metrics showing it significantly outperforming its industry peers. The crowd\'s optimism is driven by UNH\'s recent Q2 2026 earnings report, which massively beat Wall Street estimates and resulted in raised full-year guidance. On platforms like Reddit and Stocktwits, retail investors are actively praising the company as a strong "defensive" and "momentum" play, specifically highlighting better-than-feared medical costs and ongoing share buybacks.',
        onTag: 'WITH THE LUNARCRUSH PACK',
        onText:
          'Based on the recent data, the social media crowd is showing a massive surge of interest in UnitedHealth ($UNH), highlighted by an attention spike of over 500,000 interactions per hour that reached 26 times the usual volume. This explosion in engagement is driven primarily by the company\'s recent earnings beat and its strong performance leading the broader healthcare sector. Commentators are expressing highly bullish sentiment as a result, characterizing UNH as a powerful insurance monopoly and predicting that the stock could climb to $700.',
      },
    },
    {
      kind: 'qa',
      kicker: '03 — QUESTION 2',
      title: 'Why is pi network trending on social media today, and what is the crowd mood?',
      sub: 'Same model, same question, asked 2026-07-22. Left: the model alone. Right: the model with a LunarCrush context pack (downloadable below).',
      qa: {
        offTag: 'WITHOUT LIVE CONTEXT',
        offText:
          'Pi Network is trending today primarily due to the official activation of the Protocol v25 upgrade, which introduces privacy-preserving smart contracts and paves the way for a native decentralized exchange (DEX). The community is also closely watching the PI token\'s recent 25–30% "relief rally" as it rebounds from an all-time low of $0.070. Overall, the crowd mood is highly polarized; while supporters are optimistic about technological milestones and new utility products like Pi Verify, there is heavy market anxiety surrounding an impending supply unlock of over 100 million tokens.',
        onTag: 'WITH THE LUNARCRUSH PACK',
        onText:
          'Pi Network is trending due to a massive attention spike of over 1 million interactions per hour, heavily sparked by a viral diagram showing the project appearing on a "RootData" map. The momentum was further fueled by official nostalgic TikToks and highly engaged regional community meetings across Southeast Asia discussing the ecosystem\'s broader value. Despite this 53x surge in typical engagement, the measured crowd mood is completely flat, registering at exactly 0% for positive, negative, and neutral sentiment.',
      },
    },
    {
      kind: 'qa',
      kicker: '04 — QUESTION 3',
      title: 'What is the current social sentiment on tesla going into its earnings report this week, and which platforms are most positive?',
      sub: 'Same model, same question, asked 2026-07-22. Left: the model alone. Right: the model with a LunarCrush context pack (downloadable below).',
      qa: {
        offTag: 'WITHOUT LIVE CONTEXT',
        offText:
          'Social sentiment for Tesla ahead of its Q2 2026 earnings is currently described as a "cautious optimism" driven by a recent delivery beat but tempered by fears of margin erosion [1, 2]. Retail sentiment is most positive on platforms like Reddit (r/teslainvestorsclub) and X, where users are highly bullish on Tesla\'s unique "Physical AI" moat, FSD v15, and the recent July rollout of Robotaxi services in Florida [2]. Conversely, skepticism is more prominent on Stocktwits, where traders are wary of an "overpromise problem" regarding aggressive expansion timelines and high capital expenditures [5]. Options markets reflect this tension, bracing for high volatility as institutional analysts await concrete updates on Optimus commercialization and Cybercab production to justify the stock\'s stretched valuation [1, 5].',
        onTag: 'WITH THE LUNARCRUSH PACK',
        onText:
          'Heading into its earnings report, Tesla\'s social engagement over the last 24-48 hours is predominantly neutral at 66%, though positive sentiment (20%) slightly outweighs the negative (15%). This conversation is driven by massive online interest, generating over 1.6 billion interactions across social channels in just the past seven days. When breaking down sentiment by platform, Twitter (tweet) leads as the most positive network with a score of 86 out of 100. YouTube follows as the second most positive platform with a score of 72, while TikTok ranks the lowest with a nearly split score of 51.',
      },
    },
    {
      kind: 'callout',
      kicker: '05 — WHAT WE OBSERVED',
      title: '',
      heading: 'The failure mode is not ignorance, it is fluency',
      paragraphs: [
        'Asked about live markets without live data, the model did not say "I cannot know that": it produced confident, specific, plausible-sounding claims that nothing in its context could support. With a pack attached, the same model cited real numbers, real timestamps, and real posts. That is the entire argument for grounding: the ungrounded answer is not obviously wrong, which is exactly what makes it dangerous in a research workflow.',
        'Fair caveats: one model, three questions, one day. This page is a demonstration you can rerun in about a minute, not a benchmark. The packs are downloadable below; swap in your own harness and check us.',
      ],
    },
    {
      kind: 'prose',
      kicker: '06 — DO THIS WITH YOUR OWN HARNESS',
      title: 'The pack pattern is the product',
      subHtml:
        'Every pack on this page was generated from the same API that powers the commercial feed. Point your assistant at the <a href="https://lunarcrush.com/products/lunarcrush-mcpserver">LunarCrush MCP server</a> and it can build packs like these on demand, for any ticker, topic, product, or person, with live data instead of a snapshot.',
    },
    {
      kind: 'dataset',
      kicker: '07 — THE PACKS',
      title: 'Run it yourself',
      sub: 'The three context packs used above, exactly as the model saw them, timestamps included.',
      downloads: [
        { label: '⬇ pack: pi-network', file: 'context-advantage/pack-pi-network.md', format: 'md', encodingFormat: 'text/markdown', primary: true },
        { label: '⬇ pack: tesla', file: 'context-advantage/pack-tesla.md', format: 'md', encodingFormat: 'text/markdown' },
        { label: '⬇ pack: unitedhealth', file: 'context-advantage/pack-unitedhealth.md', format: 'md', encodingFormat: 'text/markdown' },
        { label: '⬇ AI guide (.md for your LLM)', file: 'context-advantage/lc_context_advantage_2026-07-22.md', format: 'md', encodingFormat: 'text/markdown' },
      ],
      downloadNote: AI_GUIDE_NOTE,
    },
  ],

  talkToJoe: {
    heading: 'Talk to Joe',
    body: 'Two-week institutional trials include API and MCP access, so your assistants and your researchers pull from the same live source. Most teams have a grounded workflow running the same week.',
    name: 'Joe Vezzani',
    role: 'Co-founder & CEO, LunarCrush',
    email: 'joe@lunarcrush.com',
    subject: 'Context Advantage',
    headshot: '/research/joe-headshot.png',
  },
};

/* ─── Study #4: The Product Sentiment Atlas ──────────────────────── */

const productSentimentAtlas: Study = {
  slug: 'product-sentiment-atlas',
  title: 'The Product Sentiment Atlas',
  hook: 'How the crowd feels about 21 consumer products: engagement sentiment + per-network mood across X, Reddit, TikTok, YouTube, Instagram. Free dataset.',
  displayDate: 'July 2026',
  datePublished: '2026-07-22',
  dateModified: '2026-07-22',
  accent: 'var(--rc-amber)',
  ogImage: `${CANONICAL_BASE}/research/product-sentiment-atlas/og-image.png`,

  metaTitle: 'The Product Sentiment Atlas — LunarCrush Research',
  metaDescription:
    'How the crowd feels about 21 consumer products: engagement sentiment + per-network mood across X, Reddit, TikTok, YouTube, Instagram. Free dataset.',
  ogTitle: 'The Product Sentiment Atlas',
  ogDescription:
    'How the crowd feels about 21 consumer products: engagement sentiment + per-network mood across X, Reddit, TikTok, YouTube, Instagram. Free dataset.',
  twitterDescription:
    '21 products, 38.6B interactions, five networks, one sentiment atlas. Free dataset, no registration.',

  hero: {
    badge: 'NEW RESEARCH · JULY 2026 · 21 PRODUCTS · FREE DATASET BELOW',
    headline: 'The Product',
    headlineAccent: 'Sentiment Atlas.',
    sub: 'How the crowd actually feels about 21 consumer products, measured from 38.6B interactions across X, Reddit, TikTok, YouTube, Instagram, and 10,000+ news sources. Not a survey: the observed mood of engagement itself.',
    stats: [
      { label: 'Products', countUp: 21, note: 'consumer, snapshot' },
      { label: 'Interactions · 7d', countUp: 38.6, decimals: 1, suffix: 'B', note: 'across all networks' },
      { label: 'Most loved', text: "McDonald's Corporation", note: '84% positive engagement' },
      { label: 'Widest platform split', countUp: 65, suffix: 'pts', note: 'same product, different network' },
    ],
  },

  blocks: [
    {
      kind: 'bars',
      kicker: '01 — THE LOVE BOARD',
      title: 'Ranked by positive engagement share',
      sub: "Share of each product's interactions that land on positive-sentiment posts (24-48h window). The bar is the crowd voting with its attention.",
      unit: '%',
      decimals: 0,
      bars: [
        { label: "McDonald's Corporation", value: 84 },
        { label: 'Temu', value: 51 },
        { label: 'Starbucks Corporation', value: 51 },
        { label: 'NIKE, Inc.', value: 47 },
        { label: 'Uber Technologies, Inc.', value: 42 },
        { label: 'XBox', value: 40 },
        { label: 'Robinhood Markets, Inc.', value: 38 },
        { label: 'OpenAI', value: 38 },
        { label: 'Playstation', value: 34 },
        { label: 'Adidas', value: 34 },
        { label: 'Macbook', value: 33 },
        { label: 'Shein', value: 32 },
      ],
    },
    {
      kind: 'table',
      kicker: '02 — THE DIVISIVE BOARD',
      title: 'Products the crowd fights about',
      sub: 'Ranked by non-neutral share: high positive AND high negative at the same time. Divisive is not bad; it is engagement you can measure both sides of.',
      table: {
        columns: [
          { key: 'product', label: 'Product', kind: 'text' },
          { key: 'pos', label: 'Positive', kind: 'text', tone: 'pos' },
          { key: 'neg', label: 'Negative', kind: 'text', tone: 'neg' },
          { key: 'neu', label: 'Neutral', kind: 'text' },
          { key: 'nn', label: 'Non-neutral', kind: 'text', tone: 'accent' },
        ],
        rows: [
          { product: "McDonald's Corporation", pos: '84%', neg: '4%', neu: '12%', nn: '88%' },
          { product: 'Starbucks Corporation', pos: '51%', neg: '9%', neu: '40%', nn: '60%' },
          { product: 'Uber Technologies, Inc.', pos: '42%', neg: '15%', neu: '43%', nn: '57%' },
          { product: 'DoorDash, Inc.', pos: '7%', neg: '49%', neu: '44%', nn: '56%' },
          { product: 'Macbook', pos: '33%', neg: '23%', neu: '44%', nn: '56%' },
          { product: 'Temu', pos: '51%', neg: '4%', neu: '45%', nn: '55%' },
          { product: 'XBox', pos: '40%', neg: '12%', neu: '48%', nn: '52%' },
          { product: 'NIKE, Inc.', pos: '47%', neg: '5%', neu: '49%', nn: '51%' },
          { product: 'OpenAI', pos: '38%', neg: '12%', neu: '50%', nn: '50%' },
          { product: 'Shein', pos: '32%', neg: '17%', neu: '51%', nn: '49%' },
        ],
      },
    },
    {
      kind: 'table',
      kicker: '03 — THE PLATFORM SPLIT',
      title: 'Same product, five different moods',
      sub: "Per-network sentiment score (0-100). The Spread column is the gap between a product's warmest and coldest platform: brand teams read rows, allocators read the column.",
      table: {
        columns: [
          { key: 'name', label: 'Name', kind: 'text' },
          { key: 'x', label: 'X', kind: 'text' },
          { key: 'reddit', label: 'Reddit', kind: 'text' },
          { key: 'tiktok', label: 'TikTok', kind: 'text' },
          { key: 'youtube', label: 'YouTube', kind: 'text' },
          { key: 'instagram', label: 'Instagram', kind: 'text' },
          { key: 'spread', label: 'Spread', kind: 'text', tone: 'accent' },
        ],
        rows: [
          { name: 'DoorDash, Inc.', x: '78', reddit: '72', tiktok: '13', youtube: '62', instagram: '–', spread: '65' },
          { name: 'Uber Technologies, Inc.', x: '31', reddit: '70', tiktok: '82', youtube: '38', instagram: '93', spread: '62' },
          { name: 'Macbook', x: '55', reddit: '82', tiktok: '78', youtube: '49', instagram: '100', spread: '51' },
          { name: 'Airbnb, Inc.', x: '51', reddit: '77', tiktok: '75', youtube: '74', instagram: '96', spread: '45' },
          { name: "McDonald's Corporation", x: '90', reddit: '56', tiktok: '100', youtube: '100', instagram: '–', spread: '44' },
          { name: 'OpenAI', x: '84', reddit: '79', tiktok: '75', youtube: '79', instagram: '43', spread: '41' },
          { name: 'iPhone', x: '86', reddit: '79', tiktok: '74', youtube: '76', instagram: '48', spread: '38' },
          { name: 'Tesla, Inc.', x: '86', reddit: '68', tiktok: '51', youtube: '72', instagram: '64', spread: '35' },
          { name: 'Rivian', x: '80', reddit: '79', tiktok: '81', youtube: '55', instagram: '90', spread: '35' },
          { name: 'Spotify Technology S.A.', x: '80', reddit: '83', tiktok: '64', youtube: '72', instagram: '94', spread: '30' },
          { name: 'NIKE, Inc.', x: '81', reddit: '82', tiktok: '94', youtube: '79', instagram: '64', spread: '30' },
          { name: 'Playstation', x: '70', reddit: '73', tiktok: '81', youtube: '78', instagram: '98', spread: '28' },
          { name: 'Robinhood Markets, Inc.', x: '86', reddit: '91', tiktok: '73', youtube: '79', instagram: '100', spread: '27' },
          { name: 'Shein', x: '92', reddit: '91', tiktok: '70', youtube: '84', instagram: '97', spread: '27' },
          { name: 'Temu', x: '73', reddit: '74', tiktok: '95', youtube: '93', instagram: '98', spread: '25' },
          { name: 'Airpods', x: '54', reddit: '78', tiktok: '69', youtube: '60', instagram: '56', spread: '24' },
          { name: 'Starbucks Corporation', x: '70', reddit: '66', tiktok: '88', youtube: '89', instagram: '90', spread: '24' },
          { name: 'XBox', x: '77', reddit: '85', tiktok: '77', youtube: '82', instagram: '100', spread: '23' },
          { name: 'Netflix, Inc.', x: '87', reddit: '77', tiktok: '68', youtube: '70', instagram: '–', spread: '19' },
          { name: 'Adidas', x: '77', reddit: '89', tiktok: '81', youtube: '84', instagram: '70', spread: '19' },
          { name: 'Red Bull', x: '75', reddit: '–', tiktok: '–', youtube: '–', instagram: '–', spread: '0' },
        ],
      },
    },
    {
      kind: 'callout',
      kicker: '04 — READ THIS BEFORE QUOTING IT',
      title: '',
      heading: 'What this measures, precisely',
      paragraphs: [
        'Sentiment here is engagement-weighted: the share of interactions landing on posts classified positive, negative, or neutral. It answers "what mood is the crowd amplifying," not "what do owners think." A product can be beloved by its users and still bleed negative engagement when one video goes viral. That distinction is exactly why the per-network split matters: the platform where the mood breaks first is usually the story.',
        'This page is a one-day snapshot. The commercial feed is the same measurement as a continuous hourly time series, per product, per network, with history.',
      ],
    },
    {
      kind: 'dataset',
      kicker: '05 — THE DATASET',
      title: "Don't take our word for it",
      sub: 'Every product in this study: interactions, sentiment shares, per-network scores. No registration.',
      downloads: [
        { label: '⬇ CSV (24 products × 5 networks)', file: 'product-sentiment-atlas/lc_product_sentiment_2026-07-22.csv', format: 'csv', encodingFormat: 'text/csv', primary: true },
        { label: '⬇ AI guide (.md for your LLM)', file: 'product-sentiment-atlas/lc_product_sentiment_2026-07-22.md', format: 'md', encodingFormat: 'text/markdown' },
      ],
      downloadNote: AI_GUIDE_NOTE,
    },
  ],

  talkToJoe: {
    heading: 'Talk to Joe',
    body: 'The commercial feed is this exact measurement as continuous hourly time series with history, for any product, brand, or ticker universe. Most teams have it in a dashboard the same week.',
    name: 'Joe Vezzani',
    role: 'Co-founder & CEO, LunarCrush',
    email: 'joe@lunarcrush.com',
    subject: 'Product Sentiment Atlas',
    headshot: '/research/joe-headshot.png',
  },

  dataset: {
    name: 'LunarCrush Product Sentiment Atlas 2026-07-22',
    description:
      'Engagement sentiment + per-network sentiment scores for 24 consumer products across X, Reddit, TikTok, YouTube, Instagram.',
  },
};

/* ─── Study #5: The People Index ─────────────────────────────────── */

const peopleIndex: Study = {
  slug: 'people-index',
  title: 'The People Index',
  hook: 'Engagement sentiment for 15 of the most-watched public figures, and the gap between how the crowd treats a CEO and the ticker. Free dataset.',
  displayDate: 'July 2026',
  datePublished: '2026-07-22',
  dateModified: '2026-07-22',
  accent: 'var(--rc-green)',
  ogImage: `${CANONICAL_BASE}/research/people-index/og-image.png`,

  metaTitle: 'The People Index — LunarCrush Research',
  metaDescription:
    'Engagement sentiment for 15 of the most-watched public figures, and the gap between how the crowd treats a CEO and the ticker. Free dataset.',
  ogTitle: 'The People Index',
  ogDescription:
    'Engagement sentiment for 15 of the most-watched public figures, and the gap between how the crowd treats a CEO and the ticker. Free dataset.',
  twitterDescription:
    'The crowd prices the person before the market prices the company. Free dataset, no registration.',

  hero: {
    badge: 'NEW RESEARCH · JULY 2026 · 15 PUBLIC FIGURES · FREE DATASET BELOW',
    headline: 'The People Index.',
    sub: 'The crowd prices the person before the market prices the company. Engagement sentiment for the most-watched executives and public figures, and the gap between how the crowd treats a CEO and how it treats the ticker.',
    stats: [
      { label: 'Public figures', countUp: 15, note: 'snapshot' },
      { label: 'Interactions · 7d', countUp: 7.8, decimals: 1, suffix: 'B', note: 'across all networks' },
      { label: 'Warmest crowd', text: 'Tim Cook', note: '98% positive engagement' },
      { label: 'Widest person-vs-company gap', countUp: 78, suffix: 'pts', note: 'see section 02' },
    ],
  },

  blocks: [
    {
      kind: 'bars',
      kicker: '01 — THE BOARD',
      title: 'Ranked by positive engagement share',
      sub: "Share of each figure's interactions landing on positive posts, 24-48h window, all five networks.",
      unit: '%',
      decimals: 0,
      bars: [
        { label: 'Tim Cook', value: 98 },
        { label: 'Lisa Su', value: 88 },
        { label: 'Taylor Swift', value: 72 },
        { label: 'Satya Nadella', value: 65 },
        { label: 'SUNDAR PICHAI', value: 63 },
        { label: 'Mark Zuckerberg', value: 53 },
        { label: 'Sam Altman', value: 41 },
        { label: 'Elon Musk', value: 37 },
        { label: 'MrBeast', value: 35 },
        { label: 'Cathie Wood', value: 35 },
        { label: 'Jensen Huang', value: 24 },
        { label: 'Warren Buffett', value: 21 },
        { label: 'Jamie Dimon', value: 15 },
        { label: 'Andy Jassy', value: 7 },
        { label: 'Jerome Powell', value: 5 },
      ],
    },
    {
      kind: 'table',
      kicker: '02 — PERSON vs COMPANY',
      title: 'The executive premium (and discount)',
      sub: "Same methodology on the person and on their company's ticker. A large gap either way is signal: the crowd is telling different stories about the leader and the business.",
      table: {
        columns: [
          { key: 'person', label: 'Person', kind: 'text' },
          { key: 'company', label: 'Company topic', kind: 'text' },
          { key: 'personPos', label: 'Person +', kind: 'text', tone: 'pos' },
          { key: 'companyPos', label: 'Company +', kind: 'text' },
          { key: 'gap', label: 'Gap', kind: 'gap' },
        ],
        rows: [
          { person: 'Lisa Su', company: 'Advanced Micro Devices, Inc.', personPos: '88%', companyPos: '10%', gap: '+78 pts' },
          { person: 'Tim Cook', company: 'Apple Inc.', personPos: '98%', companyPos: '34%', gap: '+64 pts' },
          { person: 'Jamie Dimon', company: 'JPM', personPos: '15%', companyPos: '72%', gap: '-57 pts' },
          { person: 'Andy Jassy', company: 'Amazon.com, Inc.', personPos: '7%', companyPos: '51%', gap: '-43 pts' },
          { person: 'SUNDAR PICHAI', company: 'googl', personPos: '63%', companyPos: '100%', gap: '-37 pts' },
          { person: 'Satya Nadella', company: 'Microsoft Corporation', personPos: '65%', companyPos: '36%', gap: '+29 pts' },
          { person: 'Mark Zuckerberg', company: 'Meta', personPos: '53%', companyPos: '29%', gap: '+24 pts' },
          { person: 'Elon Musk', company: 'Tesla, Inc.', personPos: '37%', companyPos: '20%', gap: '+17 pts' },
          { person: 'Jensen Huang', company: 'NVDA', personPos: '24%', companyPos: '37%', gap: '-13 pts' },
        ],
      },
    },
    {
      kind: 'table',
      kicker: '03 — THE PLATFORM SPLIT',
      title: "Where each figure's crowd lives",
      sub: 'Per-network sentiment score (0-100). The same person can run warm on TikTok and cold on X at the same hour; the spread column finds them.',
      table: {
        columns: [
          { key: 'name', label: 'Name', kind: 'text' },
          { key: 'x', label: 'X', kind: 'text' },
          { key: 'reddit', label: 'Reddit', kind: 'text' },
          { key: 'tiktok', label: 'TikTok', kind: 'text' },
          { key: 'youtube', label: 'YouTube', kind: 'text' },
          { key: 'instagram', label: 'Instagram', kind: 'text' },
          { key: 'spread', label: 'Spread', kind: 'text', tone: 'accent' },
        ],
        rows: [
          { name: 'Jamie Dimon', x: '27', reddit: '33', tiktok: '90', youtube: '82', instagram: '100', spread: '84' },
          { name: 'Warren Buffett', x: '67', reddit: '100', tiktok: '56', youtube: '76', instagram: '100', spread: '79' },
          { name: 'Jerome Powell', x: '66', reddit: '38', tiktok: '71', youtube: '65', instagram: '100', spread: '62' },
          { name: 'Tim Cook', x: '91', reddit: '50', tiktok: '100', youtube: '47', instagram: '50', spread: '53' },
          { name: 'Jensen Huang', x: '75', reddit: '100', tiktok: '76', youtube: '93', instagram: '100', spread: '50' },
          { name: 'Cathie Wood', x: '93', reddit: '100', tiktok: '75', youtube: '73', instagram: '50', spread: '50' },
          { name: 'Andy Jassy', x: '100', reddit: '50', tiktok: '100', youtube: '50', instagram: '50', spread: '50' },
          { name: 'Satya Nadella', x: '97', reddit: '50', tiktok: '50', youtube: '50', instagram: '50', spread: '47' },
          { name: 'Lisa Su', x: '93', reddit: '50', tiktok: '50', youtube: '50', instagram: '50', spread: '43' },
          { name: 'Sam Altman', x: '62', reddit: '80', tiktok: '91', youtube: '59', instagram: '100', spread: '41' },
          { name: 'MrBeast', x: '84', reddit: '66', tiktok: '66', youtube: '82', instagram: '99', spread: '33' },
          { name: 'Mark Zuckerberg', x: '83', reddit: '82', tiktok: '93', youtube: '81', instagram: '100', spread: '19' },
          { name: 'Taylor Swift', x: '88', reddit: '87', tiktok: '98', youtube: '88', instagram: '100', spread: '13' },
          { name: 'Elon Musk', x: '78', reddit: '75', tiktok: '73', youtube: '76', instagram: '84', spread: '11' },
          { name: 'SUNDAR PICHAI', x: '95', reddit: '–', tiktok: '–', youtube: '–', instagram: '–', spread: '0' },
        ],
      },
    },
    {
      kind: 'callout',
      kicker: '04 — READ THIS BEFORE QUOTING IT',
      title: '',
      heading: 'What this is, and is not',
      paragraphs: [
        'This is the observed mood of engagement, not a poll, not approval, and not a prediction. People with enormous audiences accumulate enormous neutral engagement, which suppresses every share; that is why the gap column compares like with like. Snapshot taken 2026-07-22. The commercial feed tracks the same measurement hourly, with history, for any set of people, brands, or tickers.',
      ],
    },
    {
      kind: 'dataset',
      kicker: '05 — THE DATASET',
      title: "Don't take our word for it",
      sub: 'Every figure in this study: interactions, sentiment shares, per-network scores. No registration.',
      downloads: [
        { label: '⬇ CSV (15 figures × 5 networks)', file: 'people-index/lc_people_index_2026-07-22.csv', format: 'csv', encodingFormat: 'text/csv', primary: true },
        { label: '⬇ AI guide (.md for your LLM)', file: 'people-index/lc_people_index_2026-07-22.md', format: 'md', encodingFormat: 'text/markdown' },
      ],
      downloadNote: AI_GUIDE_NOTE,
    },
  ],

  talkToJoe: {
    heading: 'Talk to Joe',
    body: 'The commercial feed runs this measurement hourly with history for any watchlist of people, brands, or tickers, with point-in-time constituent handling for universes.',
    name: 'Joe Vezzani',
    role: 'Co-founder & CEO, LunarCrush',
    email: 'joe@lunarcrush.com',
    subject: 'People Index',
    headshot: '/research/joe-headshot.png',
  },

  dataset: {
    name: 'LunarCrush People Index 2026-07-22',
    description:
      'Engagement sentiment + per-network scores for 16 public figures, with executive-vs-ticker gap methodology.',
  },
};

/* ─── Study #6: Rates: The Crowd Says War. The Curve Says the Fed. ── */

const ratesCrowdVsCurve: Study = {
  slug: 'rates-crowd-vs-curve',
  title: 'The Crowd Says War. The Curve Says the Fed.',
  hook: 'The 10-year is up 114bp since February and 91% of it is real yield. What social media blames, what the curve prices, which stocks feel it, and why the midterms barely register in bond talk. Free dataset.',
  displayDate: 'September 2026',
  datePublished: '2026-09-25',
  dateModified: '2026-09-25',
  accent: 'var(--rc-green)',
  ogImage: `${CANONICAL_BASE}/research/rates-crowd-vs-curve/og-image.png`,

  metaTitle: 'The Crowd Says War. The Curve Says the Fed. — LunarCrush Research',
  metaDescription:
    '10Y at 5.11%, 30Y at 5.40%, mortgages over 7%. Social attention vs. the Treasury curve: what the crowd blames, what the curve prices, which stocks feel it. Free dataset.',
  ogTitle: 'The Crowd Says War. The Curve Says the Fed.',
  ogDescription: '10Y at 5.11%, 30Y at 5.40%, mortgages over 7%. Social attention vs. the Treasury curve, with a free dataset.',
  twitterDescription: 'The crowd blames war and oil. The curve is pricing the Fed. Free dataset, no registration.',

  hero: {
    badge: 'NEW RESEARCH · SEPTEMBER 2026 · FREE DATASET BELOW',
    headline: 'The crowd says war. The curve says the Fed.',
    sub: 'The 10-year Treasury hit 5.11% on Sep 23, the 30-year 5.40%, and mortgage rates crossed 7%. Social media is four times louder about yields than in the first half. We put what the crowd blames next to what the curve is pricing, measured which stocks feel it, and checked how much the midterms show up in bond talk.',
    stats: [
      { label: '10Y since Feb 27', countUp: 114, prefix: '+', suffix: 'bp', note: '3.97% → 5.11%' },
      { label: 'Real-yield share', countUp: 91, suffix: '%', note: 'of the 10Y move' },
      { label: 'Yield talk vs H1', countUp: 4.3, decimals: 1, suffix: 'x', note: '"treasury yields" posts, Sep pace' },
      { label: 'Midterms in bond talk', countUp: 0.5, decimals: 1, suffix: '%', note: 'of engagement, Jan to Sep' },
    ],
  },

  blocks: [
    {
      kind: 'lines',
      kicker: '01 — THE MOVE',
      title: 'A real-rate shock, not an inflation scare',
      sub: 'From the Feb 27 low the 2-year rose 147bp, the 10-year 114bp, the 30-year 76bp. The front end led and the curve flattened. The Fed cut three times in late 2025, then hiked 25bp at its September meeting. Weekly close, %. Source: FRED.',
      unit: '%',
      labels: ['Jan 9', 'Jan 16', 'Jan 23', 'Jan 30', 'Feb 6', 'Feb 13', 'Feb 20', 'Feb 27', 'Mar 6', 'Mar 13', 'Mar 20', 'Mar 27', 'Apr 3', 'Apr 10', 'Apr 17', 'Apr 24', 'May 1', 'May 8', 'May 15', 'May 22', 'May 29', 'Jun 5', 'Jun 12', 'Jun 19', 'Jun 26', 'Jul 3', 'Jul 10', 'Jul 17', 'Jul 24', 'Jul 31', 'Aug 7', 'Aug 14', 'Aug 21', 'Aug 28', 'Sep 4', 'Sep 11', 'Sep 18', 'Sep 25'],
      series: [
        {
          label: '30Y',
          values: [4.82, 4.83, 4.82, 4.87, 4.85, 4.69, 4.72, 4.64, 4.77, 4.9, 4.96, 4.98, 4.91, 4.91, 4.88, 4.91, 4.97, 4.95, 5.12, 5.07, 4.99, 5.01, 4.97, 4.9, 4.87, 4.98, 5.06, 5.06, 5.16, 5.27, 5.19, 5.25, 5.27, 5.22, 5.24, 5.35, 5.34, 5.4],
          color: 'var(--rc-blue)',
        },
        {
          label: '10Y',
          values: [4.18, 4.24, 4.24, 4.26, 4.22, 4.04, 4.08, 3.97, 4.15, 4.28, 4.39, 4.44, 4.35, 4.31, 4.26, 4.31, 4.39, 4.38, 4.59, 4.56, 4.45, 4.55, 4.48, 4.46, 4.38, 4.49, 4.56, 4.55, 4.69, 4.75, 4.65, 4.68, 4.74, 4.73, 4.78, 4.96, 5.01, 5.11],
          color: 'var(--rc-green)',
          fill: true,
        },
        {
          label: '2Y',
          values: [3.54, 3.59, 3.6, 3.52, 3.5, 3.4, 3.48, 3.38, 3.56, 3.73, 3.88, 3.88, 3.84, 3.81, 3.71, 3.78, 3.88, 3.9, 4.09, 4.13, 3.98, 4.17, 4.09, 4.19, 4.07, 4.14, 4.21, 4.18, 4.33, 4.28, 4.19, 4.17, 4.24, 4.34, 4.37, 4.63, 4.76, 4.85],
          color: 'var(--rc-amber)',
        },
      ],
    },
    {
      kind: 'bars',
      kicker: '01b — THE DECOMPOSITION',
      title: "What the 10-year's +114bp is made of",
      sub: 'Change since Feb 27, basis points. An inflation or deficit scare lifts breakevens and steepens the curve. This did the opposite.',
      unit: 'bp',
      decimals: 0,
      bars: [
        { label: '2Y nominal', value: 147 },
        { label: '30Y mortgage', value: 105 },
        { label: '10Y real yield (TIPS)', value: 104 },
        { label: '10Y inflation expectations', value: 8 },
        { label: '5Y inflation expectations', value: -7 },
      ],
    },
    {
      kind: 'lines',
      kicker: '02 — THE CONVERSATION',
      title: 'Four times louder, and it follows the tape',
      sub: '"Treasury yields" + "bond market" posts per week (full weeks) vs. the 10-year. H1 averaged ~3,300 treasury-yield posts a month; September is running ~14,200. Day by day, chatter follows yield moves more than it leads them.',
      unit: 'posts',
      labels: ['Jan 9', 'Jan 16', 'Jan 23', 'Jan 30', 'Feb 6', 'Feb 13', 'Feb 20', 'Feb 27', 'Mar 6', 'Mar 13', 'Mar 20', 'Mar 27', 'Apr 3', 'Apr 10', 'Apr 17', 'Apr 24', 'May 1', 'May 8', 'May 15', 'May 22', 'May 29', 'Jun 5', 'Jun 12', 'Jun 19', 'Jun 26', 'Jul 3', 'Jul 10', 'Jul 17', 'Jul 24', 'Jul 31', 'Aug 7', 'Aug 14', 'Aug 21', 'Aug 28', 'Sep 4', 'Sep 11', 'Sep 18', 'Sep 25'],
      series: [
        {
          label: 'Rate posts / week',
          values: [1376, 1784, 3999, 2701, 1743, 2362, 1991, 1660, 1914, 1712, 1852, 3268, 2378, 1657, 1590, 1238, 1983, 2004, 4343, 7284, 3056, 3041, 2622, 2229, 2704, 1650, 1790, 2462, 2757, 2770, 2901, 3268, 12043, 10288, 8408, 6713, 7719, null],
          color: 'var(--rc-blue)',
          type: 'bar',
        },
        {
          label: '10Y yield (%)',
          values: [4.18, 4.24, 4.24, 4.26, 4.22, 4.04, 4.08, 3.97, 4.15, 4.28, 4.39, 4.44, 4.35, 4.31, 4.26, 4.31, 4.39, 4.38, 4.59, 4.56, 4.45, 4.55, 4.48, 4.46, 4.38, 4.49, 4.56, 4.55, 4.69, 4.75, 4.65, 4.68, 4.74, 4.73, 4.78, 4.96, 5.01, 5.11],
          color: 'var(--rc-green)',
          axis: 'right',
        },
      ],
    },
    {
      kind: 'bars',
      kicker: '03 — WHAT THE CROWD BLAMES',
      title: 'War and oil lead the story. The Fed trails it.',
      sub: 'Share of engagement on 16,899 rate-specific posts (Jan 1 to Sep 23, 1.7B interactions) citing each driver. Keyword-tagged, overlapping; most posts cite none.',
      unit: '%',
      decimals: 1,
      bars: [
        { label: 'War / oil', value: 18.8 },
        { label: 'The Fed', value: 8.6 },
        { label: 'Inflation', value: 8.3 },
        { label: 'Japan / global bonds', value: 7.9 },
        { label: 'AI capex', value: 6.6 },
        { label: 'Deficits / debt supply', value: 4.8 },
        { label: 'Housing / mortgages', value: 3.3 },
        { label: 'Midterms', value: 0.5 },
      ],
    },
    {
      kind: 'table',
      kicker: '03b — CROWD vs CURVE',
      title: 'Who is right',
      sub: 'What the crowd emphasises vs. what market pricing shows.',
      table: {
        columns: [
          { key: 'driver', label: 'Driver', kind: 'text' },
          { key: 'crowd', label: 'Crowd', kind: 'text' },
          { key: 'curve', label: 'Curve says', kind: 'text', tone: 'accent' },
        ],
        rows: [
          { driver: 'War / oil', crowd: '#1 by far (19%)', curve: 'Breakevens flat: not pricing lasting inflation' },
          { driver: 'Inflation', crowd: '~8%', curve: '5Y breakeven down 7bp' },
          { driver: 'The Fed', crowd: '~9%', curve: '2Y +147bp, curve flatter: the main channel' },
          { driver: 'Deficits', crowd: '~5%', curve: 'Would steepen the curve; it flattened' },
          { driver: 'Midterms', crowd: '0.5%', curve: 'Not priced yet' },
        ],
      },
    },
    {
      kind: 'lines',
      kicker: '04 — THE MIDTERMS',
      title: 'Two loud conversations that barely touch',
      sub: 'Midterm posts are up ~6x since January, to a pace near 300,000 a month, yet only 0.5% of bond-market engagement mentions them. Where they meet, it is affordability, gas prices and the national debt. After Nov 3 the channel that matters is the fiscal path. Weekly posts, log scale.',
      unit: 'posts',
      scale: 'log',
      labels: ['Jan 9', 'Jan 16', 'Jan 23', 'Jan 30', 'Feb 6', 'Feb 13', 'Feb 20', 'Feb 27', 'Mar 6', 'Mar 13', 'Mar 20', 'Mar 27', 'Apr 3', 'Apr 10', 'Apr 17', 'Apr 24', 'May 1', 'May 8', 'May 15', 'May 22', 'May 29', 'Jun 5', 'Jun 12', 'Jun 19', 'Jun 26', 'Jul 3', 'Jul 10', 'Jul 17', 'Jul 24', 'Jul 31', 'Aug 7', 'Aug 14', 'Aug 21', 'Aug 28', 'Sep 4', 'Sep 11', 'Sep 18', 'Sep 25'],
      series: [
        {
          label: 'Midterms',
          values: [8538, 9920, 11712, 14594, 11136, 11790, 12261, 13103, 13240, 12335, 11027, 12586, 14528, 14374, 15061, 15353, 13559, 20910, 26583, 22714, 21850, 16304, 20241, 23460, 22678, 26076, 21438, 31584, 28037, 28645, 27797, 24143, 32930, 53958, 54264, 68537, 77768, null],
          color: 'var(--rc-red)',
        },
        {
          label: 'Treasury yields',
          values: [490, 583, 984, 682, 436, 759, 646, 593, 779, 573, 669, 1022, 771, 539, 420, 346, 533, 535, 896, 2786, 1030, 989, 919, 756, 991, 581, 673, 1018, 1226, 1026, 1247, 1580, 4772, 2920, 3346, 2751, 3358, null],
          color: 'var(--rc-blue)',
        },
      ],
    },
    {
      kind: 'bars',
      kicker: '05 — WHO FEELS IT',
      title: 'Housing breaks first. Energy moves with yields.',
      sub: 'Median daily % move per +10bp in the 10-year, by sector. 923 of the 1,000 largest US stocks, Mar 1 to Sep 22. Energy is the only sector that rises with yields.',
      unit: '%',
      decimals: 2,
      bars: [
        { label: 'Basic Materials', value: -2.1 },
        { label: 'Consumer Cyclical', value: -1.6 },
        { label: 'Industrials', value: -1.42 },
        { label: 'Technology', value: -1.4 },
        { label: 'Healthcare', value: -1.09 },
        { label: 'Defense', value: -1.07 },
        { label: 'Banks', value: -1.01 },
        { label: 'Communication Services', value: -0.81 },
        { label: 'Real Estate', value: -0.79 },
        { label: 'Consumer Defensive', value: -0.73 },
        { label: 'Financial Services', value: -0.6 },
        { label: 'Utilities', value: -0.49 },
        { label: 'Energy', value: 1.33 },
      ],
    },
    {
      kind: 'table',
      kicker: '05b — TIGHTEST LINKS',
      title: 'Large caps that track the 10-year most closely',
      sub: 'Market cap over $20B. Correlation of daily return with daily 10Y change. Airlines and cruise lines share the oil driver.',
      table: {
        columns: [
          { key: 'symbol', label: 'Ticker', kind: 'symbol' },
          { key: 'name', label: 'Company', kind: 'text' },
          { key: 'corr', label: 'Corr.', kind: 'text' },
          { key: 'per10', label: 'Per +10bp day', kind: 'text', tone: 'accent' },
          { key: 'ret', label: 'Since Feb 27', kind: 'move' },
        ],
        rows: [
          { symbol: 'RKT', name: 'Rocket Companies, Inc.', corr: '-0.70', per10: '-5.8%', ret: -0.3205 },
          { symbol: 'DHI', name: 'D.R. Horton, Inc.', corr: '-0.63', per10: '-3.1%', ret: -0.1015 },
          { symbol: 'LEN', name: 'Lennar Corporation', corr: '-0.63', per10: '-3.3%', ret: -0.3172 },
          { symbol: 'CCL', name: 'Carnival Corporation & plc', corr: '-0.62', per10: '-4.3%', ret: -0.2938 },
          { symbol: 'PHM', name: 'PulteGroup, Inc.', corr: '-0.61', per10: '-2.9%', ret: -0.1087 },
          { symbol: 'UAL', name: 'United Airlines Holdings, In', corr: '-0.61', per10: '-4.2%', ret: 0.0838 },
          { symbol: 'OXY', name: 'Occidental Petroleum Corpora', corr: '+0.55', per10: '+2.8%', ret: 0.0786 },
          { symbol: 'COP', name: 'ConocoPhillips', corr: '+0.54', per10: '+2.4%', ret: 0.124 },
          { symbol: 'DVN', name: 'Devon Energy Corporation', corr: '+0.52', per10: '+2.6%', ret: 0.0933 },
        ],
      },
    },
    {
      kind: 'callout',
      kicker: '06 — WHAT TO WATCH INTO NOVEMBER',
      title: '',
      heading: 'Four gauges that settle the argument',
      paragraphs: [
        '5-year breakeven: if the war-and-oil story is right, it rises. So far it is lower than in February; a move above 2.5% would change the read.',
        'The 2s30s curve: flattening says the Fed. A steepener with the long end leading would say fiscal worry and term premium.',
        'House-control odds: the midterm signal for rates is who controls Congress, not the affordability debate. One-party control raises the odds of a bigger deficit.',
        'Early-November refunding and the late-October FOMC: Treasury\'s borrowing plan lands around the election; the Fed meets days before it. Watch whether social attention shifts from war to deficits.',
      ],
    },
    {
      kind: 'callout',
      kicker: '07 — READ THIS BEFORE QUOTING IT',
      title: '',
      heading: 'What this is, and is not',
      paragraphs: [
        'Social shares measure attention, not cause. Driver tags are keyword-based and overlapping; most posts cite no driver. Stock sensitivities are correlations over one regime, and oil moves both yields and several of these stocks. Yields from FRED as of Sep 23-24, 2026; LunarCrush closes were spot-checked against an independent source. This is research on public data, not investment advice or a rate forecast.',
      ],
    },
    {
      kind: 'dataset',
      kicker: '08 — THE DATASET',
      title: "Don't take our word for it",
      sub: 'Every figure in this study. No registration.',
      downloads: [
        { label: '⬇ Daily yields + social volume (CSV)', file: 'rates-crowd-vs-curve/lc_rates_daily_2026.csv', format: 'csv', encodingFormat: 'text/csv', primary: true },
        { label: '⬇ 923-stock rate sensitivity (CSV)', file: 'rates-crowd-vs-curve/lc_rates_stock_sensitivity.csv', format: 'csv', encodingFormat: 'text/csv' },
        { label: '⬇ Sector sensitivity (CSV)', file: 'rates-crowd-vs-curve/lc_rates_sector_sensitivity.csv', format: 'csv', encodingFormat: 'text/csv' },
        { label: '⬇ AI guide (.md for your LLM)', file: 'rates-crowd-vs-curve/lc_rates_ai_guide.md', format: 'md', encodingFormat: 'text/markdown' },
      ],
      downloadNote: AI_GUIDE_NOTE,
    },
  ],

  talkToJoe: {
    heading: 'Talk to Joe',
    body: 'The commercial feed runs this hourly with history: attention, sentiment and narrative for any ticker, topic or watchlist, point-in-time.',
    name: 'Joe Vezzani',
    role: 'Co-founder & CEO, LunarCrush',
    email: 'joe@lunarcrush.com',
    subject: 'Rates research',
    headshot: '/research/joe-headshot.png',
  },

  dataset: {
    name: 'LunarCrush Rates: Crowd vs. Curve 2026-09-25',
    description:
      'Daily Treasury yields, breakevens and mortgage rates alongside LunarCrush social post volume for rate and midterm topics, plus 10Y sensitivity for 923 US stocks.',
  },
};

/* ─── Study #7: The Binance Hack, Reconstructed ──────────────────── */

const binanceHackReconstructed: Study = {
  slug: 'binance-hack-reconstructed',
  title: 'The Binance Hack, Reconstructed',
  hook: 'A rehearsed attack, a four-hour open door, and a crowd that rebuilt it before Binance spoke: every step from the chain and 53,181 posts.',
  displayDate: 'October 2026',
  datePublished: '2026-10-30',
  dateModified: '2026-10-30',
  accent: 'var(--rc-red)',
  ogImage: `${CANONICAL_BASE}/research/binance-hack-reconstructed/og-image.png`,

  metaTitle: 'The Binance Hack, Reconstructed | LunarCrush Research',
  metaDescription: 'A step-by-step reconstruction of the Oct 29, 2026 Binance hot-wallet hack from the Ethereum record and 53,181 social posts: setup, drain, response and where the money is.',
  ogTitle: 'The Binance Hack, Reconstructed',
  ogDescription: 'Rehearsed on mainnet, one sweep for $0.93 in gas, attacker code live for 3h 49m. Every step from the chain and the public record, with the full report.',
  twitterDescription: 'Every step of the Binance hack, from the chain and the public record. Full 77-page report.',

  hero: {
    badge: 'NEW RESEARCH · OCTOBER 2026 · FULL REPORT BELOW',
    headline: 'The Binance Hack,',
    headlineAccent: 'Reconstructed.',
    sub: 'On the evening of October 29, a wallet funded only through Tornado Cash attached its own code to Binance 14, one of the exchange\'s main Ethereum hot wallets, and swept it in a single transaction. Every step had been rehearsed on mainnet that afternoon. The attacker\'s code then stayed on the wallet for 3 hours 49 minutes. Stablecoin issuers froze what they could inside the first hour. Binance said at 00:13 UTC that it would cover all losses, and at 00:52 that about $3 billion was frozen.',
    stats: [
      { label: 'Rehearsal lead', text: '6h 29m', note: 'from the first rehearsal to the delegation' },
      { label: 'Authorizations', text: '1 of 50', note: 'signed authorizations took effect' },
      { label: 'Open door', text: '3h 49m', note: 'attacker code live on Binance 14' },
      { label: 'Crowd vs chain', text: '8 min', note: 'from the sweep to the first public post' },
    ],
  },

  blocks: [
    {
      kind: 'gantt',
      kicker: '01 · THE WHOLE HACK ON ONE PAGE',
      title: 'The whole hack on one page',
      sub: 'Six lanes, one clock: the setup on the left, the five-hour attack on the right at about 9.5 times the scale. Hover, tap or use the arrow keys to step through every event.',
      ariaLabel: 'Timeline of the Binance hack, October 28 to 30, 2026, across funding, rehearsal, attack, issuer freezes, Binance and social lanes',
      lanes: [{ key: 'fund', label: 'Funding', tone: 'muted' }, { key: 'prep', label: 'Build and rehearse', tone: 'blue' }, { key: 'attack', label: 'Attacker on Binance 14', tone: 'red' }, { key: 'issuer', label: 'Issuer freezes', tone: 'green' }, { key: 'binance', label: 'Binance', tone: 'amber' }, { key: 'social', label: 'Social', tone: 'violet' }],
      segments: [{ from: '2026-10-28T06:00:00Z', to: '2026-10-29T20:00:00Z', weight: 1, label: 'Setup · Oct 28 06:00 to Oct 29 20:00' }, { from: '2026-10-29T20:00:00Z', to: '2026-10-30T01:05:00Z', weight: 1.25, label: 'The attack night' }],
      bands: [{ from: '2026-10-29T20:20:59Z', to: '2026-10-30T00:10:35Z', lane: 'attack', label: 'attacker code live · 3h 49m', tone: 'red' }],
      notes: [{ lane: 'social', from: '2026-10-28T06:00:00Z', to: '2026-10-29T19:00:00Z', text: 'no public post about any of it' }, { lane: 'attack', from: '2026-10-28T06:00:00Z', to: '2026-10-29T12:00:00Z', text: 'Binance 14 operating normally' }],
      ticks: [{ t: '2026-10-28T06:00:00Z', label: 'Oct 28 06:00' }, { t: '2026-10-28T18:00:00Z', label: 'Oct 28 18:00' }, { t: '2026-10-29T06:00:00Z', label: 'Oct 29 06:00' }, { t: '2026-10-29T20:00:00Z', label: '20:00' }, { t: '2026-10-29T21:00:00Z', label: '21:00' }, { t: '2026-10-29T22:00:00Z', label: '22:00' }, { t: '2026-10-29T23:00:00Z', label: '23:00' }, { t: '2026-10-30T00:00:00Z', label: '00:00' }, { t: '2026-10-30T01:00:00Z', label: '01:00' }],
      events: [
          { t: '2026-10-28T07:39:00Z', lane: 'fund', time: 'Oct 28 07:39', title: 'Tornado Cash withdrawal to the operator', detail: '0.1 ETH from the Tornado Cash pool to 0x7C86…5CA0; another at 08:53 and a third at 08:23 Oct 29.', seen: '@spreekaway, 20:32 Oct 29', short: 'Tornado Cash funding', labelPos: 'above' },
          { t: '2026-10-28T08:53:00Z', lane: 'fund', time: 'Oct 28 08:53', title: 'Second Tornado Cash withdrawal', detail: '0.1 ETH to the operator.' },
          { t: '2026-10-29T01:15:00Z', lane: 'fund', time: 'Oct 29 01:15', title: 'Sibling wallet funded', detail: '0xFD41 receives 0.1 ETH from Tornado Cash.' },
          { t: '2026-10-29T07:08:00Z', lane: 'fund', time: 'Oct 29 07:08', title: 'Sibling wallet funded', detail: '0xe4F9 receives 0.1 ETH from Tornado Cash.' },
          { t: '2026-10-29T08:23:00Z', lane: 'fund', time: 'Oct 29 08:23', title: 'Third Tornado Cash withdrawal', detail: '0.1 ETH to the operator. About 1.49 ETH in total funded the operation.' },
          { t: '2026-10-29T14:54:00Z', lane: 'fund', time: 'Oct 29 14:54', title: '1 ETH pool withdrawal', detail: '0xe4F9 receives 0.994 ETH from the Tornado Cash 1 ETH pool.' },
          { t: '2026-10-29T18:20:47Z', lane: 'fund', time: 'Oct 29 18:20:47', title: 'Gas for the real run', detail: '0xe4F9 sends the operator 1.082 ETH.', seen: 'no post' },
          { t: '2026-10-29T02:28:59Z', lane: 'prep', time: 'Oct 29 02:28:59', title: 'Sweeper v1 deployed', detail: 'The first sweeper contract, 0x19bE…f2bf, never verified on any explorer.', seen: '@MetaFinancialAI, 21:31', short: 'Sweeper v1', labelPos: 'above' },
          { t: '2026-10-29T13:51:35Z', end: '2026-10-29T14:24:11Z', lane: 'prep', time: 'Oct 29 13:51', title: 'Rehearsal one', detail: 'Two of the operator\'s own wallets delegated with EIP-7702, seeded with a few dollars of USDT and USDC, and swept.', seen: '@MetaFinancialAI, 21:31; @danupsher, 21:47', short: 'Rehearsal 1', labelPos: 'below' },
          { t: '2026-10-29T15:59:11Z', lane: 'prep', time: 'Oct 29 15:59:11', title: 'Sweeper v2 deployed', detail: 'The production contract, 0x63e8…6fAe: operator-only, leaves exactly 1 ETH, with an execute passthrough and a withdraw.', seen: '@0xQuit, 21:09; @blockaid_, 21:25' },
          { t: '2026-10-29T16:20:47Z', end: '2026-10-29T16:47:23Z', lane: 'prep', time: 'Oct 29 16:20', title: 'Rehearsal two, the full sequence', detail: 'Delegate, fund with 1.01 ETH, sweep (1 ETH left), withdraw. The exact sequence used on Binance 14.', seen: '@MetaFinancialAI, 21:31', short: 'Rehearsal 2', labelPos: 'above' },
          { t: '2026-10-29T20:41:23Z', lane: 'attack', time: 'Oct 29 20:41:23', title: '780M USDC through a trusted wallet', detail: 'Hot Wallet 34 accepts Binance 14 as its owner and releases 780M USDC to 0x5227.', seen: '@0xfoobar, 20:48; @kethcode, 22:08' },
          { t: '2026-10-29T20:59:47Z', lane: 'attack', time: 'Oct 29 20:59:47', title: '43M USDC more', detail: 'The same route through Hot Wallet 34.', seen: 'no sampled post' },
          { t: '2026-10-29T21:04:00Z', lane: 'attack', time: 'Oct 29 21:04', title: 'First re-sweep', detail: '3.9M USDT plus dust.', seen: '@uttam_singhk, 22:00' },
          { t: '2026-10-29T21:44:00Z', lane: 'attack', time: 'Oct 29 21:44', title: 'Second re-sweep', detail: '200.4M USDT, 6,591 LINK, 7,008 UNI and smaller lines.', seen: '@uttam_singhk, 22:00' },
          { t: '2026-10-29T22:39:00Z', lane: 'attack', time: 'Oct 29 22:39', title: 'The long tail', detail: 'Smaller tokens worth at least $7.5M on the ten lines shown, to a second receiver.', seen: 'no sampled post' },
          { t: '2026-10-29T23:36:00Z', end: '2026-10-29T23:56:00Z', lane: 'attack', time: 'Oct 29 23:36', title: 'Six empty sweeps', detail: 'The operator calls sweep six more times. Nothing moves.', seen: 'no post' },
          { t: '2026-10-29T21:05:00Z', lane: 'issuer', time: 'Oct 29 21:05', title: 'Tether freezes $971M', detail: 'First reported by @TreeNewsFeed, ten minutes before any Binance account spoke.', seen: '1.99M engagements', short: 'First freeze 21:05', labelPos: 'above' },
          { t: '2026-10-29T21:13:00Z', lane: 'issuer', time: 'Oct 29 21:13', title: 'Circle freezes 823M USDC', detail: 'Jeremy Allaire confirms more than 800M USDC frozen at 21:18, in coordination with law enforcement (per The Block).', seen: '@WatcherGuru, 21:13' },
          { t: '2026-10-29T21:52:00Z', lane: 'issuer', time: 'Oct 29 21:52', title: 'Aave freezes its USDT market', detail: 'Aave freezes its USDT market, blocking new borrowing against it, per @WatcherGuru.', seen: '@WatcherGuru, 21:52 (222K)' },
          { t: '2026-10-29T22:08:00Z', lane: 'binance', time: 'Oct 29 22:08', title: 'Binance thanks Ripple', detail: 'Yi He thanks Ripple for freezing \'approximately 200 million\' (unit not stated); no freeze shows on the traced XRP account.', seen: '@heyibinance' },
          { t: '2026-10-29T22:22:00Z', lane: 'issuer', time: 'Oct 29 22:22', title: 'World Liberty freezes', detail: '$27M USD1 and 48M WLFI frozen, per Yi He.', seen: '@heyibinance' },
          { t: '2026-10-29T21:15:00Z', lane: 'binance', time: 'Oct 29 21:15', title: 'CZ: \'It does look like a hack\'', detail: '\'User funds are safe!\' The most-engaged post of the event. @binance posts holding statements at 21:15, 21:16 and 21:24.', seen: '3.19M engagements', short: 'First word 21:15', labelPos: 'above' },
          { t: '2026-10-29T21:29:00Z', lane: 'binance', time: 'Oct 29 21:29', title: '\'User funds are completely safe\'', detail: '@binance reposts its statement with one added sentence; it draws ten times the engagement.', seen: '904K engagements' },
          { t: '2026-10-29T22:23:00Z', lane: 'binance', time: 'Oct 29 22:23', title: '\'Will cover\'', detail: 'CZ: \'Loss might be a bit higher than that, but will cover.\'', seen: '926K engagements' },
          { t: '2026-10-29T22:32:00Z', lane: 'binance', time: 'Oct 29 22:32', title: 'Some withdrawals processed', detail: 'Per CryptoTicker, some BTC, ETH and USDT withdrawals were processed; Binance\'s 22:33 notice said withdrawals would resume once the issue was resolved.', seen: 'per CryptoTicker' },
          { t: '2026-10-29T23:06:00Z', lane: 'binance', time: 'Oct 29 23:06', title: 'Binance Alpha trading paused', detail: 'Suspended as a security measure.', seen: '243K engagements' },
          { t: '2026-10-29T23:22:00Z', lane: 'binance', time: 'Oct 29 23:22', title: '$2.54B verified frozen', detail: 'Binance addresses \'differing figures circulating publicly\'.', seen: '529K engagements' },
          { t: '2026-10-30T00:10:35Z', lane: 'binance', time: 'Oct 30 00:10:35', title: 'Binance 14 revokes the delegation', detail: 'A type-4 transaction pointing the delegation at the zero address clears the attacker\'s code after 3h 49m.', seen: 'ChainCatcher, 00:44' },
          { t: '2026-10-30T00:13:00Z', lane: 'binance', time: 'Oct 30 00:13', title: 'Full pause; will cover all losses', detail: '\'As a precaution, all deposits and withdrawals are temporarily suspended.\'', seen: '378K engagements' },
          { t: '2026-10-30T00:52:00Z', lane: 'binance', time: 'Oct 30 00:52', title: 'About $3B verified frozen', detail: '\'The current verified total frozen amount is around $3 billion.\'', seen: '156K engagements' },
          { t: '2026-10-29T20:32:00Z', lane: 'social', time: 'Oct 29 20:32', title: 'First \'hack\' post', detail: '@spreekaway, 39.5K followers: \'Unconfirmed, but possibly massive binance hack.\' 1.6M engagements.', seen: '8 minutes after the sweep', short: 'Crowd has it 20:32', labelPos: 'above' },
          { t: '2026-10-29T20:54:00Z', lane: 'social', time: 'Oct 29 20:54', title: 'Mechanism explained', detail: '@odysseas_eth lays out the EIP-7702 sweeper and the 63-token sweep.', seen: '218K engagements' },
          { t: '2026-10-29T21:09:00Z', lane: 'social', time: 'Oct 29 21:09', title: 'Full reconstruction', detail: '@0xQuit has the deploy time and the four-step sequence right.', seen: '492K engagements' },
          { t: '2026-10-29T21:31:00Z', lane: 'social', time: 'Oct 29 21:31', title: 'Rehearsals found', detail: '@MetaFinancialAI posts the rehearsal timeline, 67 minutes after the sweep.', seen: '1.5K engagements' },
          { t: '2026-10-29T21:42:00Z', lane: 'social', time: 'Oct 29 21:42', title: '@WatcherGuru: \'$5.6B stolen\'', detail: 'An unconfirmed outflow estimate; reported figures ranged from $2.53B to $5.7B.', seen: '1.98M engagements' },
      ],
      caption: 'Every mark is a verified transaction or a timestamped public statement.',
    },
    {
      kind: 'steps',
      kicker: '02 · ACT I · THE SETUP',
      title: 'Eighteen hours from the first contract to the drain, all on a public chain, with no post about any of it',
      sub: 'Everything the attack needed was built and tested in the open on October 28 and 29: funding, two contracts and two rehearsals, the second a full run.',
      tone: 'blue',
      steps: [
        { time: 'Oct 28 07:39', title: 'Funded through Tornado Cash', body: 'Two 0.1 ETH withdrawals from the Tornado Cash pool reach the operator 0x7C86…5CA0, at 07:39 and 08:53; a third follows at 08:23 on Oct 29. Two sibling wallets, 0xFD41 and 0xe4F9, are funded the same way. About 1.49 ETH in total: the operation\'s entire budget.', seen: '@spreekaway, 20:32 Oct 29' },
        { time: 'Oct 29 02:28', title: 'Sweeper v1 deployed', body: 'The operator deploys its first sweeper contract, 0x19be…f2bf. It has never been verified on any explorer.', seen: '@MetaFinancialAI, 21:31' },
        { time: '13:51', title: 'Rehearsal one', body: 'Two fresh wallets controlled by the operator are delegated to the sweeper with EIP-7702 transactions, seeded with a few dollars of USDT and USDC, and swept at 14:19 and 14:24.', seen: '@MetaFinancialAI, 21:31; @danupsher, 21:47' },
        { time: '15:59', title: 'Sweeper v2 deployed', body: 'The production contract, 0x63e8…6fAe. Only the operator can call it. Its sweep leaves exactly 1 ETH behind, and it has an execute passthrough and a withdraw function.', seen: '@0xQuit, 21:09; @blockaid_, 21:25' },
        { time: '16:20', title: 'Rehearsal two, the full sequence', body: 'A third test wallet is delegated, funded with 1.01 ETH, swept at 16:30 (0.01 ETH out, 1 ETH left) and emptied with a withdraw call at 16:47. The exact sequence used on Binance 14 three and a half hours later.', seen: '@MetaFinancialAI, 21:31' },
        { time: '18:20', title: 'Funding for the real run', body: '0xe4F9 sends the operator 1.082 ETH.', seen: 'no public post found' },
      ],
    },
    {
      kind: 'gantt',
      kicker: '03 · THE ATTACK NIGHT',
      title: '20:00 to 01:00 UTC, lane by lane',
      sub: 'Chain, issuers, Binance, the crowd and the market on one clock. The shaded window is the 3 hours 49 minutes Binance 14 ran the attacker\'s code.',
      ariaLabel: 'Lane-by-lane timeline of the Binance hack night, 20:00 Oct 29 to 01:00 Oct 30 UTC',
      lanes: [{ key: 'other', label: 'Other chains*', tone: 'muted' }, { key: 'attack', label: 'Attacker on Binance 14', tone: 'red' }, { key: 'issuer', label: 'Issuer freezes', tone: 'green' }, { key: 'binance', label: 'Binance', tone: 'amber' }, { key: 'social', label: 'Social', tone: 'violet' }, { key: 'market', label: 'Market', tone: 'muted' }],
      segments: [{ from: '2026-10-29T20:00:00Z', to: '2026-10-30T01:05:00Z', weight: 1, label: 'October 29 to 30, UTC' }],
      bands: [{ from: '2026-10-29T20:20:59Z', to: '2026-10-30T00:10:35Z', label: 'attacker code live on Binance 14 · 3h 49m', tone: 'red' }],
      ticks: [{ t: '2026-10-29T20:00:00Z', label: '20:00' }, { t: '2026-10-29T20:30:00Z', label: '20:30' }, { t: '2026-10-29T21:00:00Z', label: '21:00' }, { t: '2026-10-29T21:30:00Z', label: '21:30' }, { t: '2026-10-29T22:00:00Z', label: '22:00' }, { t: '2026-10-29T22:30:00Z', label: '22:30' }, { t: '2026-10-29T23:00:00Z', label: '23:00' }, { t: '2026-10-29T23:30:00Z', label: '23:30' }, { t: '2026-10-30T00:00:00Z', label: '00:00' }, { t: '2026-10-30T00:30:00Z', label: '00:30' }, { t: '2026-10-30T01:00:00Z', label: '01:00' }],
      events: [
          { t: '2026-10-29T20:05:00Z', lane: 'other', time: 'Oct 29 20:05', title: '185,017,485 XRP ($236M) to a new account', detail: 'XRP Ledger transfer from accounts Whale Alert labels Binance. Binance has not said whether it was its own.', seen: '@hackenclub linked it, 20:51', short: 'XRP $236M*', labelPos: 'above' },
          { t: '2026-10-29T20:10:18Z', lane: 'other', time: 'Oct 29 20:10:18', title: '965,455 SOL ($95M) to a new address', detail: 'Solana transfer from a wallet Whale Alert labels Binance.', seen: '@hackenclub linked it, 20:51', short: 'SOL $95M*', labelPos: 'below' },
          { t: '2026-10-29T20:20:59Z', lane: 'attack', time: 'Oct 29 20:20:59', title: 'The delegation', detail: 'One transaction carries 50 EIP-7702 authorizations, all signed by Binance 14\'s key. The one matching its live nonce (18914809) applies.', seen: '@odysseas_eth, 20:54; @0xQuit, 21:21', short: 'Delegation', labelPos: 'above' },
          { t: '2026-10-29T20:24:23Z', lane: 'attack', time: 'Oct 29 20:24:23', title: 'The sweep', detail: '167,464 ETH and 63 tokens to 0x5227…2f8c in one call, for about $0.93 in gas. Largest line: 1.615B USDT.', seen: '@spreekaway, 20:32 (+8 min)', short: 'Sweep', labelPos: 'below' },
          { t: '2026-10-29T20:41:23Z', lane: 'attack', time: 'Oct 29 20:41:23', title: '780M USDC through a trusted wallet', detail: 'Hot Wallet 34 accepts Binance 14 as its owner and releases 780M USDC to 0x5227.', seen: '@0xfoobar, 20:48; @kethcode, 22:08' },
          { t: '2026-10-29T20:59:47Z', lane: 'attack', time: 'Oct 29 20:59:47', title: '43M USDC more', detail: 'The same route through Hot Wallet 34.', seen: 'no sampled post' },
          { t: '2026-10-29T21:04:00Z', lane: 'attack', time: 'Oct 29 21:04', title: 'First re-sweep', detail: '3.9M USDT plus dust.', seen: '@uttam_singhk, 22:00' },
          { t: '2026-10-29T21:44:00Z', lane: 'attack', time: 'Oct 29 21:44', title: 'Second re-sweep', detail: '200.4M USDT, 6,591 LINK, 7,008 UNI and smaller lines.', seen: '@uttam_singhk, 22:00', short: '200.4M USDT', labelPos: 'above' },
          { t: '2026-10-29T22:39:00Z', lane: 'attack', time: 'Oct 29 22:39', title: 'The long tail', detail: 'Smaller tokens worth at least $7.5M on the ten lines shown, to a second receiver.', seen: 'no sampled post' },
          { t: '2026-10-29T23:36:00Z', end: '2026-10-29T23:56:00Z', lane: 'attack', time: 'Oct 29 23:36', title: 'Six empty sweeps', detail: 'The operator calls sweep six more times. Nothing moves.', seen: 'no post' },
          { t: '2026-10-29T21:05:00Z', lane: 'issuer', time: 'Oct 29 21:05', title: 'Tether freezes $971M', detail: 'First reported by @TreeNewsFeed, ten minutes before any Binance account spoke.', seen: '1.99M engagements', short: 'Tether $971M', labelPos: 'above' },
          { t: '2026-10-29T21:13:00Z', lane: 'issuer', time: 'Oct 29 21:13', title: 'Circle freezes 823M USDC', detail: 'Jeremy Allaire confirms more than 800M USDC frozen at 21:18, in coordination with law enforcement (per The Block).', seen: '@WatcherGuru, 21:13' },
          { t: '2026-10-29T21:25:00Z', lane: 'issuer', time: 'Oct 29 21:25', title: 'Tether raises its freeze to 1.567B USDT', detail: 'Paolo Ardoino: \'Tether froze more than 1.567B USDT related to a potential Binance exchange hack.\'', seen: '1.03M engagements' },
          { t: '2026-10-29T21:52:00Z', lane: 'issuer', time: 'Oct 29 21:52', title: 'Aave freezes its USDT market', detail: 'Aave freezes its USDT market, blocking new borrowing against it, per @WatcherGuru.', seen: '@WatcherGuru, 21:52 (222K)' },
          { t: '2026-10-29T22:08:00Z', lane: 'binance', time: 'Oct 29 22:08', title: 'Binance thanks Ripple', detail: 'Yi He thanks Ripple for freezing \'approximately 200 million\' (unit not stated); no freeze shows on the traced XRP account.', seen: '@heyibinance' },
          { t: '2026-10-29T22:22:00Z', lane: 'issuer', time: 'Oct 29 22:22', title: 'World Liberty freezes', detail: '$27M USD1 and 48M WLFI frozen, per Yi He.', seen: '@heyibinance' },
          { t: '2026-10-29T21:15:00Z', lane: 'binance', time: 'Oct 29 21:15', title: 'CZ: \'It does look like a hack\'', detail: '\'User funds are safe!\' The most-engaged post of the event. @binance posts holding statements at 21:15, 21:16 and 21:24.', seen: '3.19M engagements', short: 'CZ: looks like a hack', labelPos: 'above' },
          { t: '2026-10-29T21:29:00Z', lane: 'binance', time: 'Oct 29 21:29', title: '\'User funds are completely safe\'', detail: '@binance reposts its statement with one added sentence; it draws ten times the engagement.', seen: '904K engagements' },
          { t: '2026-10-29T22:23:00Z', lane: 'binance', time: 'Oct 29 22:23', title: '\'Will cover\'', detail: 'CZ: \'Loss might be a bit higher than that, but will cover.\'', seen: '926K engagements' },
          { t: '2026-10-29T22:32:00Z', lane: 'binance', time: 'Oct 29 22:32', title: 'Some withdrawals processed', detail: 'Per CryptoTicker, some BTC, ETH and USDT withdrawals were processed; Binance\'s 22:33 notice said withdrawals would resume once the issue was resolved.', seen: 'per CryptoTicker' },
          { t: '2026-10-29T23:06:00Z', lane: 'binance', time: 'Oct 29 23:06', title: 'Binance Alpha trading paused', detail: 'Suspended as a security measure.', seen: '243K engagements' },
          { t: '2026-10-29T23:22:00Z', lane: 'binance', time: 'Oct 29 23:22', title: '$2.54B verified frozen', detail: 'Binance addresses \'differing figures circulating publicly\'.', seen: '529K engagements', short: '$2.54B verified', labelPos: 'above' },
          { t: '2026-10-30T00:10:35Z', lane: 'binance', time: 'Oct 30 00:10:35', title: 'Binance 14 revokes the delegation', detail: 'A type-4 transaction pointing the delegation at the zero address clears the attacker\'s code after 3h 49m.', seen: 'ChainCatcher, 00:44' },
          { t: '2026-10-30T00:13:00Z', lane: 'binance', time: 'Oct 30 00:13', title: 'Full pause; will cover all losses', detail: '\'As a precaution, all deposits and withdrawals are temporarily suspended.\'', seen: '378K engagements' },
          { t: '2026-10-30T00:52:00Z', lane: 'binance', time: 'Oct 30 00:52', title: 'About $3B verified frozen', detail: '\'The current verified total frozen amount is around $3 billion.\'', seen: '156K engagements' },
          { t: '2026-10-29T20:32:00Z', lane: 'social', time: 'Oct 29 20:32', title: 'First \'hack\' post', detail: '@spreekaway, 39.5K followers: \'Unconfirmed, but possibly massive binance hack.\' 1.6M engagements.', seen: '8 minutes after the sweep', short: 'First post', labelPos: 'above' },
          { t: '2026-10-29T20:54:00Z', lane: 'social', time: 'Oct 29 20:54', title: 'Mechanism explained', detail: '@odysseas_eth lays out the EIP-7702 sweeper and the 63-token sweep.', seen: '218K engagements' },
          { t: '2026-10-29T21:09:00Z', lane: 'social', time: 'Oct 29 21:09', title: 'Full reconstruction', detail: '@0xQuit has the deploy time and the four-step sequence right.', seen: '492K engagements' },
          { t: '2026-10-29T21:31:00Z', lane: 'social', time: 'Oct 29 21:31', title: 'Rehearsals found', detail: '@MetaFinancialAI posts the rehearsal timeline, 67 minutes after the sweep.', seen: '1.5K engagements' },
          { t: '2026-10-29T21:42:00Z', lane: 'social', time: 'Oct 29 21:42', title: '@WatcherGuru: \'$5.6B stolen\'', detail: 'An unconfirmed outflow estimate; reported figures ranged from $2.53B to $5.7B.', seen: '1.98M engagements' },
          { t: '2026-10-29T22:33:00Z', lane: 'social', time: 'Oct 29 22:33', title: 'Musk: \'Everyday is Zeroday\'', detail: 'A quote-post of @KobeissiLetter\'s $5.7B estimate.', seen: '2.10M engagements', short: 'Musk', labelPos: 'below' },
          { t: '2026-10-29T20:50:00Z', lane: 'market', time: 'Oct 29 20:50', title: 'BNB evening low, $692.32', detail: 'From $738.90 at 20:15 (CoinMarketCap via CryptoTicker).', short: 'BNB low', labelPos: 'above' },
          { t: '2026-10-29T21:00:00Z', lane: 'market', time: 'Oct 29 21:00', title: 'BTC low, $78,763', detail: 'From $80,327 at 20:15. About $628M of leveraged positions closed within the hour (CoinGlass via PANews).', short: 'BTC low', labelPos: 'below' },
      ],
      caption: '*XRP and SOL senders are Binance by Whale Alert\'s label; Binance has not said whether these were its own transfers.',
    },
    {
      kind: 'steps',
      kicker: '04 · ACT II · THE DRAIN',
      title: 'One delegation, one sweep: 167,464 ETH and 63 tokens left Binance 14 for $0.93 in gas',
      sub: 'The attack on Ethereum took 3 minutes 24 seconds from delegation to sweep. Within 40 minutes a second wallet had released 823M USDC to the same address.',
      tone: 'red',
      steps: [
        { time: '20:05', title: '$331M of outflows on two other chains', body: '185,017,485 XRP ($236M) leaves Binance-labeled accounts for a new XRP Ledger account at 20:05, and 965,455 SOL ($95M) moves to a new Solana address at 20:10. Whale Alert labels the senders as Binance; Binance has not said whether these were its own transfers.', seen: '@hackenclub linked them, 20:51' },
        { time: '20:20:59', title: 'The delegation', body: 'One transaction from the operator to Binance 14 (0x28C6…1d60) carries 50 EIP-7702 authorizations. All 50 recover to Binance 14\'s own key. The one matching the wallet\'s live nonce, 18914809, applies; Binance 14\'s next transaction uses 18914810. The wallet now runs the attacker\'s code.', seen: '@odysseas_eth, 20:54; @0xQuit, 21:21' },
        { time: '20:24:23', title: 'The sweep', body: 'One call moves 167,464 ETH and 63 tokens to 0x5227…2f8c, later labeled Binance Hot Wallet Exploiter 2. The largest lines: 1.615B USDT, 62.6M RLUSD, 29,934 wBETH, 11,372 XAUt. Gas: 0.000414 ETH, about $0.93.', seen: '@spreekaway, 20:32 (+8 min)' },
        { time: '20:41:23', title: '780M USDC through a trusted wallet', body: 'Using the execute passthrough, a call leaves Binance 14 to Hot Wallet 34 (0xEe7a…), a proxy that accepts Binance 14 as its owner. It releases 780M USDC to 0x5227.', seen: '@0xfoobar, 20:48; @kethcode, 22:08' },
        { time: '20:59:47', title: '43M USDC more', body: 'The same route releases another 43M USDC.', seen: 'no sampled post' },
      ],
    },
    {
      kind: 'figure',
      kicker: '05 · ACT III · THE OPEN DOOR',
      title: 'For 3 hours 49 minutes the wallet ran the attacker\'s code and kept receiving funds',
      sub: 'Each sweep left exactly 1 ETH, enough for Binance 14 to keep paying for its own transactions. It kept transacting, kept looking normal, and kept taking in transfers.',
      src: 'binance-hack-reconstructed/hijack.png',
      alt: 'Value leaving Binance 14 while it ran the attacker\'s code, log scale',
      caption: 'Value leaving Binance 14 and Hot Wallet 34, log scale. The sweep bar counts the USDT and ETH lines only; 62 smaller tokens moved with them.',
    },
    {
      kind: 'steps',
      kicker: '06 · ACT III · STEP BY STEP',
      title: 'For 3 hours 49 minutes the wallet ran the attacker\'s code and kept receiving funds',
      sub: 'Each sweep left exactly 1 ETH, enough for Binance 14 to keep paying for its own transactions. It kept transacting, kept looking normal, and kept taking in transfers.',
      tone: 'red',
      steps: [
        { time: '20:21 to 00:10', title: 'Transfers keep arriving', body: 'Blockscout records 533 incoming token transfers to Binance 14 while it is delegated, including 205.7M USDT, 180B PEPE and 859M SHIB. Its own transactions run from nonce 18914810 to 18914903: the key holder is still transacting throughout.', seen: '@uttam_singhk, 22:00' },
        { time: '21:04', title: 'First re-sweep', body: '3.9M USDT plus dust.', seen: '@uttam_singhk on the mechanism, 22:00' },
        { time: '21:44', title: 'Second re-sweep', body: '200.4M USDT, 6,591 LINK, 7,008 UNI and smaller lines.', seen: '@uttam_singhk, 22:00' },
        { time: '22:39', title: 'The long tail', body: 'A basket of smaller tokens, worth at least $7.5M on the ten largest lines the explorer lists, goes to a second receiver, 0xA97b….', seen: 'no sampled post' },
        { time: '23:36', title: 'Six empty sweeps', body: 'Between 23:36 and 23:56 the operator calls sweep six more times. Nothing moves.', seen: 'no sampled post' },
        { time: '00:10:35', title: 'Revocation', body: 'Binance 14 signs its own type-4 (EIP-7702) transaction pointing its delegation at the zero address, clearing the attacker\'s code 3 hours 49 minutes after it landed. Binance 14\'s own outgoing transfers between 00:47 and 01:53 fail.', seen: 'ChainCatcher, 00:44' },
      ],
    },
    {
      kind: 'figure',
      kicker: '07 · ACT IV · THE RESPONSE',
      title: 'The issuers froze first; Binance\'s verified frozen total reached about $3 billion by 00:52',
      sub: 'Tether\'s first freeze reached the feed ten minutes before any Binance account spoke. Over the next four hours, issuers froze in sequence and Binance moved from holding statements to a full pause.',
      src: 'binance-hack-reconstructed/freeze_ladder.png',
      alt: 'Running sum of announced stablecoin freezes against Binance\'s verified frozen totals',
      caption: 'Green: running sum of issuer announcements (Ripple\'s ~200M excluded). Amber: totals Binance verified.',
    },
    {
      kind: 'steps',
      kicker: '08 · ACT IV · STEP BY STEP',
      title: 'The issuers froze first; Binance\'s verified frozen total reached about $3 billion by 00:52',
      sub: 'Tether\'s first freeze reached the feed ten minutes before any Binance account spoke. Over the next four hours, issuers froze in sequence and Binance moved from holding statements to a full pause.',
      tone: 'green',
      steps: [
        { time: '21:05', title: 'Tether freezes $971M', body: 'First reported by @TreeNewsFeed: \'Tether freezes $971M USDT in likely Binance hack, with no update from Binance still.\'', seen: '1.99M engagements' },
        { time: '21:13', title: 'Circle freezes 823M USDC', body: 'Jeremy Allaire confirms more than 800M USDC frozen at 21:18, in coordination with law enforcement (per The Block).', seen: '@WatcherGuru, 21:13' },
        { time: '21:15', title: 'CZ: \'It does look like a hack\'', body: '\'User funds are safe! It does look like a hack on Binance. Appreciate the quick freeze from Tether (~$971m) and Circle (~$823m).\' The most-engaged post of the event. @binance posts three holding statements at 21:15, 21:16 and 21:24.', seen: '3.19M engagements' },
        { time: '21:25', title: 'Tether raises its freeze to 1.567B USDT', body: 'Paolo Ardoino: \'Tether froze more than 1.567B USDT related to a potential Binance exchange hack.\'', seen: '1.03M engagements' },
        { time: '21:29', title: '\'User funds are completely safe\'', body: '@binance reposts its statement with one added sentence. It draws ten times the engagement of the versions without it.', seen: '904K engagements' },
        { time: '21:52', title: 'Aave freezes its USDT market', body: 'Aave freezes its USDT market, blocking new borrowing against it, per @WatcherGuru.', seen: '@WatcherGuru, 21:52 (222K)' },
        { time: '22:08', title: 'Binance thanks Ripple; World Liberty freezes', body: 'Yi He thanks Ripple for freezing \'approximately 200 million\' (unit not stated; no freeze shows on the traced XRP account), and at 22:22 World Liberty Financial for $27M USD1 and 48M WLFI.', seen: '@heyibinance' },
        { time: '22:23', title: '\'Will cover\'', body: 'CZ, replying to a net-loss estimate of about $1B: \'Loss might be a bit higher than that, but will cover.\'', seen: '926K engagements' },
        { time: '22:32', title: 'Some withdrawals processed', body: 'Per CryptoTicker, some BTC, ETH and USDT withdrawals were being processed at 22:32; Binance\'s 22:33 notice said withdrawals would resume once the issue was resolved. Binance Alpha trading is suspended at 23:06.', seen: 'CryptoTicker; @binance, 22:33 (351K)' },
        { time: '23:22', title: '$2.54B verified frozen', body: 'Binance addresses \'differing figures circulating publicly\' and puts the verified frozen total at $2.54B.', seen: '529K engagements' },
        { time: '00:13', title: 'Full pause; about $3B frozen', body: '\'Binance will cover all losses. As a precaution, all deposits and withdrawals are temporarily suspended.\' At 00:52: \'The current verified total frozen amount is around $3 billion.\'', seen: '378K and 156K engagements' },
      ],
    },
    {
      kind: 'figure',
      kicker: '09 · ACT V · THE MONEY',
      title: 'About $2.66B is frozen or recovered; about $2.83B sits unfrozen in traced addresses',
      sub: 'LunarCrush followed the funds across Ethereum, Bitcoin, Tron, XRP Ledger, Solana and Arbitrum and re-read every balance live between 03:20 and 03:27 UTC on Oct 30. Prices are CoinGecko spot at 03:22 UTC; stablecoins at face value.',
      src: 'binance-hack-reconstructed/flow.png',
      alt: 'Flow of traced funds by chain into frozen or unfrozen status',
      caption: 'US dollars at CoinGecko spot, 03:22 UTC Oct 30. The BTC, Tron, XRP and SOL legs are attributed to Binance by Whale Alert labels and timing.',
    },
    {
      kind: 'table',
      kicker: '10 · EVERY POSITION, READ LIVE',
      title: 'Balances read 03:20 to 03:27 UTC, October 30',
      table: {
        columns: [
          { key: 'where', label: 'Chain / address', kind: 'text', tone: 'accent' },
          { key: 'asset', label: 'Asset and amount', kind: 'text' },
          { key: 'usd', label: 'USD', kind: 'text' },
          { key: 'status', label: 'Status', kind: 'text', tone: 'muted' },
        ],
        rows: [
          { where: 'Bitcoin, 19 fresh addresses', asset: '18,270.2 BTC (17 × 1,000 + 832.5 + 437.7)', usd: '$1,411M', status: 'Held, unfrozen, no output spent; Whale Alert attribution' },
          { where: 'Ethereum 0‌x5227…2f8c', asset: '1,175.5M USDT + 823M USDC', usd: '$1,998M', status: 'Frozen (blacklists read from contracts)' },
          { where: 'Ethereum 0‌x8532…43d4', asset: '197,460 ETH', usd: '$436M', status: 'Never sent a transaction' },
          { where: 'Tron TG5iBMQP', asset: '312.7M USDT', usd: '$313M', status: 'Frozen (blacklisted); Whale Alert attribution' },
          { where: 'Ethereum 0‌xc887…286a', asset: '137,602 ETH', usd: '$304M', status: 'Never sent a transaction' },
          { where: 'XRP Ledger rK21t6nG', asset: '233.5M XRP', usd: '$287M', status: 'No outgoing transaction; Whale Alert attribution' },
          { where: 'Ethereum 0‌xAa24…54a0', asset: '175.0M USDT', usd: '$175M', status: 'Frozen (blacklisted)' },
          { where: 'Ethereum 0‌x897C…8df3', asset: '78,415 ETH (57,892 via 0xCcBE…a263)', usd: '$173M', status: 'Never sent a transaction' },
          { where: 'Solana AffzFDKZ', asset: '970,545 SOL (965,455 received)', usd: '$93M', status: 'No outgoing SOL; Whale Alert attribution' },
          { where: 'Ethereum 0‌x7f9d…15e3', asset: '29,934 wBETH', usd: '$73M', status: 'Unfrozen; also forwarded 15,292 ETH' },
          { where: 'Tron TCzBTkqn', asset: '66.0M USDT', usd: '$66M', status: 'Frozen (blacklisted); Whale Alert attribution' },
          { where: 'Ethereum 0‌x5227…2f8c', asset: '12,574 XAUt + 27.8M USD1', usd: '$78M', status: 'Frozen (isBlocked / frozen read from contracts)' },
          { where: 'Ethereum 0‌x5227…2f8c', asset: '16,501 ETH', usd: '$36M', status: 'Unfrozen, still swapping tokens to ETH' },
          { where: 'Arbitrum 0‌x3E2b…32d5', asset: '8,426 ETH + 74.56M ARB moved out 00:18', usd: '$30M', status: 'Recovered per @zachxbt (Arbitrum Security Council)' },
          { where: 'Ethereum 0‌x8d53…639a (Aave)', asset: '60.0M aUSDT vs 19,763 WETH debt', usd: '$60M gross, $16.3M net', status: 'Unfrozen; reserve freeze blocks new supply and borrowing, not withdrawals' },
        ],
      },
    },
    {
      kind: 'figure',
      kicker: '11 · ACT VI · THE PUBLIC RECORD',
      title: 'Each step of the drain was public within about an hour; the setup waited 5 to 37 hours',
      sub: 'Independent analysts reconstructed the attack from the chain in real time. The record they built is the spine of this report. The sweep was public in 8 minutes and the 780M USDC pull in 7. By 21:25, about an hour after the delegation, posts had the mechanism, the contract, the hardcoded operator and the fifty authorizations, and every one of those claims checks out against the chain. The setup was a different story. The first sweeper sat on-chain for 19 hours before anyone wrote about it, and both rehearsals were found only after the money had moved. The same pattern, a mixer-funded address delegating test wallets to unverified code and sweeping them, was visible from 13:51.',
      src: 'binance-hack-reconstructed/detect_lag.png',
      alt: 'Minutes from each on-chain step to the first public post describing it, log scale',
      caption: 'Minutes from each on-chain event to the first public post that described it, log scale. Blue: setup. Red: the attack.',
    },
    {
      kind: 'callout',
      kicker: '12 · HOW WE BUILT IT',
      title: 'Sources and method',
      heading: 'Every number traces to a transaction, a live balance read or a timestamped post',
      paragraphs: [
        'Ethereum: transactions, receipts, contract code and the 50 authorizations were decoded from a public JSON-RPC node; address histories and token transfers came from the Blockscout API.',
        'Balances were read live between 03:20 and 03:27 UTC on October 30 on Ethereum, Bitcoin, Tron, XRP Ledger, Solana and Arbitrum. Freeze status was read from each token contract. Prices are CoinGecko spot at 03:22 UTC; stablecoins at face value. The Aave position is collateral net of debt and counts as unfrozen.',
        'Social: LunarCrush topic, keyword and post data across X, TikTok, YouTube, Instagram, Reddit and news, pulled 01:00 to 01:30 UTC on October 30. Each on-chain step is matched to the first public post that described it.',
        'The Bitcoin, Tron, XRP and Solana senders are attributed to Binance by Whale Alert labels. Loss figures are attributed to whoever published them.',
      ],
    },
    {
      kind: 'dataset',
      kicker: '13 · THE FULL REPORT AND DATA',
      title: 'Read the 77-page reconstruction, take the data',
      sub: 'Six acts, the social record in fourteen chapters, the technical record with every transaction, and every table behind this page.',
      downloads: [
        { label: '⬇ The full report (PDF)', file: 'binance-hack-reconstructed/LunarCrush-Binance-Hack-Reconstructed.pdf', format: 'pdf', encodingFormat: 'application/pdf', primary: true },
        { label: '⬇ Every timeline event (Parquet)', file: 'binance-hack-reconstructed/lc_binance_hack_events.parquet', format: 'parquet', encodingFormat: 'application/vnd.apache.parquet' },
        { label: '⬇ Top posts and accounts (Parquet)', file: 'binance-hack-reconstructed/lc_binance_hack_top_posts.parquet', format: 'parquet', encodingFormat: 'application/vnd.apache.parquet' },
        { label: '⬇ Verified positions (CSV)', file: 'binance-hack-reconstructed/lc_binance_hack_positions.csv', format: 'csv', encodingFormat: 'text/csv' },
        { label: '⬇ Step-by-step timeline (CSV)', file: 'binance-hack-reconstructed/lc_binance_hack_timeline.csv', format: 'csv', encodingFormat: 'text/csv' },
        { label: '⬇ AI guide (.md)', file: 'binance-hack-reconstructed/lc_binance_hack_ai_guide.md', format: 'md', encodingFormat: 'text/markdown' },
      ],
      downloadNote: 'Every table also ships in the other format: swap .csv and .parquet in any file name. The build code and all ~220 raw on-chain reads, with a replay script, are on GitHub: github.com/JoeVezzani/binance-hack-reconstructed.',
      methodology: 'Social data through 01:00 UTC Oct 30, 2026. On-chain balances read live 03:20 to 03:27 UTC Oct 30.',
    },
  ],

  dataset: {
    name: 'LunarCrush: The Binance Hack, Reconstructed 2026-10-30',
    description: 'Step-by-step timeline of the Oct 29, 2026 Binance hot-wallet hack and every traced position, read live on six chains.',
    temporalCoverage: '2026-10-28/2026-10-30',
  },
};

/* ─── Study: The 2026 Midterms on Social, a Look Back ─────────────── */

const midterms2026Lookback: Study = {
  "slug": "midterms-2026-social-lookback",
  "title": "The July Break: How Social Sentiment Moved Before the 2026 Midterms",
  "hook": "For 33 of 34 weeks Republicans held the better tone in social conversation. In July that edge ended, the race became a coin flip, and from Oct 30 Democrats led every day to the vote. A year of party sentiment next to Kalshi and the polls, plus a with-and-without search test. Free dataset.",
  "displayDate": "November 2026",
  "datePublished": "2026-11-04",
  "dateModified": "2026-11-04",
  "accent": "var(--rc-blue)",
  "metaTitle": "The July Break: How Social Sentiment Moved Before the 2026 Midterms | LunarCrush Research",
  "metaDescription": "365 days of social sentiment on both US parties before the 2026 midterms, lined up against Kalshi and the polling averages, with election night by the hour and a free dataset.",
  "ogTitle": "The July Break: How Social Sentiment Moved Before the 2026 Midterms",
  "ogDescription": "Republicans held the better social tone 33 of 34 weeks. In July it ended. A year of party sentiment next to the markets and the polls, with a free dataset.",
  "twitterDescription": "A year of party sentiment before the 2026 midterms, next to Kalshi and the polls. Free dataset, no registration.",
  "hero": {
    "badge": "NEW RESEARCH · NOVEMBER 2026 · FREE DATASET BELOW",
    "headline": "The July break.",
    "headlineAccent": "How social sentiment moved before the 2026 midterms.",
    "sub": "Democrats won the House and the Senate on November 3. For most of the year the social conversation looked different: Republicans held the better tone in 33 of 34 weeks, while Democrats drew more of the volume. In the first week of July that edge ended. Over the next four months the gap averaged +0.2 and Republicans led 6 of 17 weeks; from October 30 Democrats led every day to the vote. We pulled 365 days of party sentiment from LunarCrush and lined it up with Kalshi and the polling averages to see what each signal showed, and when.",
    "stats": [
      {
        "label": "Weeks Republicans led",
        "text": "33 of 34",
        "note": "social sentiment, Nov 2025 to Jun 2026"
      },
      {
        "label": "After the break",
        "text": "6 of 17",
        "note": "weeks Republicans led, Jul to Oct"
      },
      {
        "label": "Final stretch",
        "text": "5 for 5",
        "note": "days Democrats led, Oct 30 to Nov 3"
      },
      {
        "label": "Web search alone",
        "text": "0 of 5",
        "note": "questions answered with a measured figure"
      }
    ]
  },
  "dataset": {
    "name": "LunarCrush: The 2026 Midterms on Social, a Look Back",
    "description": "Daily social metrics (posts, creators, engagement, sentiment) for 24 political and issue topics from Nov 2025 to Nov 4, 2026, a weekly Democrat vs Republican sentiment panel, and hourly party sentiment for the final week.",
    "temporalCoverage": "2025-11-05/2026-11-04",
    "variableMeasured": [
      "posts_created",
      "posts_active",
      "creators_active",
      "interactions",
      "sentiment"
    ]
  },
  "ogImage": `${CANONICAL_BASE}/research/midterms-2026-social-lookback/og-image.png`,
  "blocks": [
    {
      "kicker": "01 · THE YEAR",
      "title": "Eight months of Republican advantage, then near-even",
      "sub": "Weekly sentiment on posts about each party: the engagement-weighted share of positive engagement, 0-100, Nov 2025 through election day (the final point is Mon Nov 2 and Tue Nov 3). From November to June, Republicans led in 33 of 34 weeks, by 6.6 points on average. In the week of July 6 both parties fell and Republicans fell further. From July 6 through October 26, Republicans led 6 of 17 weeks and the average gap was +0.2. Democrats led the weeks of October 26 and November 2 by 2.8 and 3.6.",
      "kind": "lines",
      "unit": "sentiment",
      "labels": [
        "Nov 10",
        "Nov 17",
        "Nov 24",
        "Dec 1",
        "Dec 8",
        "Dec 15",
        "Dec 22",
        "Dec 29",
        "Jan 5",
        "Jan 12",
        "Jan 19",
        "Jan 26",
        "Feb 2",
        "Feb 9",
        "Feb 16",
        "Feb 23",
        "Mar 2",
        "Mar 9",
        "Mar 16",
        "Mar 23",
        "Mar 30",
        "Apr 6",
        "Apr 13",
        "Apr 20",
        "Apr 27",
        "May 4",
        "May 11",
        "May 18",
        "May 25",
        "Jun 1",
        "Jun 8",
        "Jun 15",
        "Jun 22",
        "Jun 29",
        "Jul 6",
        "Jul 13",
        "Jul 20",
        "Jul 27",
        "Aug 3",
        "Aug 10",
        "Aug 17",
        "Aug 24",
        "Aug 31",
        "Sep 7",
        "Sep 14",
        "Sep 21",
        "Sep 28",
        "Oct 5",
        "Oct 12",
        "Oct 19",
        "Oct 26",
        "Nov 2"
      ],
      "series": [
        {
          "label": "Republicans",
          "values": [
            50.3,
            50.9,
            50.6,
            55.6,
            50.4,
            47.3,
            54.1,
            55.9,
            42.6,
            52.5,
            50.5,
            43.1,
            48.0,
            67.3,
            59.1,
            60.8,
            57.8,
            59.8,
            62.2,
            58.9,
            61.7,
            64.1,
            68.6,
            68.3,
            64.5,
            65.7,
            66.0,
            61.8,
            62.9,
            64.4,
            66.6,
            65.3,
            65.8,
            66.8,
            41.7,
            41.9,
            46.9,
            47.2,
            47.2,
            45.2,
            47.3,
            42.5,
            45.1,
            61.8,
            49.9,
            41.3,
            49.0,
            51.0,
            47.9,
            54.3,
            52.6,
            61.0
          ],
          "color": "var(--rc-red)"
        },
        {
          "label": "Democrats",
          "values": [
            43.8,
            46.4,
            38.5,
            46.3,
            48.4,
            45.9,
            48.8,
            37.4,
            34.2,
            46.0,
            41.9,
            47.1,
            46.4,
            61.3,
            55.6,
            54.8,
            51.9,
            50.8,
            54.0,
            52.6,
            57.2,
            52.5,
            56.9,
            55.0,
            58.4,
            60.1,
            61.2,
            60.8,
            50.4,
            58.8,
            61.8,
            60.3,
            60.4,
            60.0,
            43.4,
            44.7,
            47.2,
            45.7,
            48.5,
            49.6,
            50.2,
            45.4,
            44.8,
            48.1,
            49.5,
            44.2,
            49.9,
            48.0,
            50.7,
            51.0,
            55.4,
            64.6
          ],
          "color": "var(--rc-blue)"
        }
      ]
    },
    {
      "kind": "table",
      "kicker": "02 · NOT A MEASUREMENT CHANGE",
      "title": "July hit political topics, not everything",
      "sub": "Engagement-weighted sentiment, the two weeks before July 6 against the two weeks after. Non-political topics moved by less than 4 points and Kalshi rose, so the drop is not a platform-wide shift. It was concentrated in party and election conversation, and Republicans fell the most.",
      "table": {
        "columns": [
          {
            "key": "t",
            "label": "Topic",
            "kind": "text",
            "tone": "accent"
          },
          {
            "key": "a",
            "label": "Jun 22 to Jul 5",
            "kind": "num"
          },
          {
            "key": "b",
            "label": "Jul 6 to Jul 19",
            "kind": "num"
          },
          {
            "key": "d",
            "label": "Change",
            "kind": "gap"
          }
        ],
        "rows": [
          {
            "t": "Republicans",
            "a": 66,
            "b": 42,
            "d": "-24.5 pts"
          },
          {
            "t": "Democrats",
            "a": 60,
            "b": 44,
            "d": "-16.2 pts"
          },
          {
            "t": "Election",
            "a": 76,
            "b": 60,
            "d": "-15.7 pts"
          },
          {
            "t": "Trump",
            "a": 62,
            "b": 57,
            "d": "-5.3 pts"
          },
          {
            "t": "Economy",
            "a": 71,
            "b": 67,
            "d": "-3.9 pts"
          },
          {
            "t": "Texas",
            "a": 68,
            "b": 66,
            "d": "-2.0 pts"
          },
          {
            "t": "Inflation",
            "a": 65,
            "b": 63,
            "d": "-1.5 pts"
          },
          {
            "t": "Kalshi",
            "a": 72,
            "b": 75,
            "d": "+3.2 pts"
          }
        ]
      }
    },
    {
      "kicker": "03 · VOLUME IS NOT TONE",
      "title": "Democrats drew more of the volume almost every week",
      "sub": "Bars: Democratic minus Republican sentiment, points. Line: Democrats' share of combined party engagement. Democrats took more than half of party engagement in 51 of 52 weeks, including the eight months they trailed on tone. More posts about a party is not more support for it; the sign of the gap is the more useful read of tone about each party, though not of support for it.",
      "kind": "lines",
      "unit": "pts",
      "labels": [
        "Nov 10",
        "Nov 17",
        "Nov 24",
        "Dec 1",
        "Dec 8",
        "Dec 15",
        "Dec 22",
        "Dec 29",
        "Jan 5",
        "Jan 12",
        "Jan 19",
        "Jan 26",
        "Feb 2",
        "Feb 9",
        "Feb 16",
        "Feb 23",
        "Mar 2",
        "Mar 9",
        "Mar 16",
        "Mar 23",
        "Mar 30",
        "Apr 6",
        "Apr 13",
        "Apr 20",
        "Apr 27",
        "May 4",
        "May 11",
        "May 18",
        "May 25",
        "Jun 1",
        "Jun 8",
        "Jun 15",
        "Jun 22",
        "Jun 29",
        "Jul 6",
        "Jul 13",
        "Jul 20",
        "Jul 27",
        "Aug 3",
        "Aug 10",
        "Aug 17",
        "Aug 24",
        "Aug 31",
        "Sep 7",
        "Sep 14",
        "Sep 21",
        "Sep 28",
        "Oct 5",
        "Oct 12",
        "Oct 19",
        "Oct 26",
        "Nov 2"
      ],
      "series": [
        {
          "label": "Democrats minus Republicans, sentiment pts",
          "values": [
            -6.5,
            -4.5,
            -12.1,
            -9.3,
            -2.0,
            -1.4,
            -5.3,
            -18.5,
            -8.4,
            -6.5,
            -8.6,
            4.0,
            -1.6,
            -6.0,
            -3.5,
            -6.0,
            -5.9,
            -9.0,
            -8.2,
            -6.3,
            -4.5,
            -11.6,
            -11.7,
            -13.3,
            -6.1,
            -5.6,
            -4.8,
            -1.0,
            -12.5,
            -5.6,
            -4.8,
            -5.0,
            -5.4,
            -6.8,
            1.7,
            2.8,
            0.3,
            -1.5,
            1.3,
            4.4,
            2.9,
            2.9,
            -0.3,
            -13.7,
            -0.4,
            2.9,
            0.9,
            -3.0,
            2.8,
            -3.3,
            2.8,
            3.6
          ],
          "color": "var(--rc-blue)",
          "negColor": "var(--rc-red)",
          "type": "bar"
        },
        {
          "label": "Democratic share of party engagement (%)",
          "values": [
            63.2,
            60.0,
            62.3,
            60.7,
            63.2,
            59.4,
            66.1,
            63.1,
            66.5,
            60.5,
            62.3,
            65.7,
            61.4,
            64.1,
            59.4,
            66.8,
            61.1,
            61.7,
            59.4,
            59.7,
            60.3,
            62.4,
            58.8,
            59.7,
            55.3,
            56.1,
            57.1,
            58.2,
            62.5,
            56.4,
            60.6,
            57.3,
            61.4,
            59.7,
            59.8,
            62.5,
            57.2,
            58.4,
            58.2,
            61.9,
            57.4,
            59.4,
            57.2,
            43.9,
            54.6,
            55.7,
            52.5,
            55.8,
            56.0,
            55.8,
            62.6,
            61.4
          ],
          "color": "var(--rc-amber)",
          "axis": "right"
        }
      ]
    },
    {
      "kicker": "04 · SOCIAL AND THE MARKET",
      "title": "They turned a week apart, then moved together",
      "sub": "Bars: the weekly sentiment gap. Line: Kalshi's price for Democratic Senate control, last daily close of each week (final point: the Nov 2 close). Kalshi hit its summer low of 40% on July 2; the Republican sentiment edge ended the following week. Kalshi crossed 60% on Sep 19 and stayed there. Week to week, the gap did not predict the next week's market move (right on direction 45% of the time), but the two moved together within the same week (correlation 0.34). Social moved with the market, not ahead of it.",
      "kind": "lines",
      "unit": "pts",
      "labels": [
        "Nov 10",
        "Nov 17",
        "Nov 24",
        "Dec 1",
        "Dec 8",
        "Dec 15",
        "Dec 22",
        "Dec 29",
        "Jan 5",
        "Jan 12",
        "Jan 19",
        "Jan 26",
        "Feb 2",
        "Feb 9",
        "Feb 16",
        "Feb 23",
        "Mar 2",
        "Mar 9",
        "Mar 16",
        "Mar 23",
        "Mar 30",
        "Apr 6",
        "Apr 13",
        "Apr 20",
        "Apr 27",
        "May 4",
        "May 11",
        "May 18",
        "May 25",
        "Jun 1",
        "Jun 8",
        "Jun 15",
        "Jun 22",
        "Jun 29",
        "Jul 6",
        "Jul 13",
        "Jul 20",
        "Jul 27",
        "Aug 3",
        "Aug 10",
        "Aug 17",
        "Aug 24",
        "Aug 31",
        "Sep 7",
        "Sep 14",
        "Sep 21",
        "Sep 28",
        "Oct 5",
        "Oct 12",
        "Oct 19",
        "Oct 26",
        "Nov 2"
      ],
      "series": [
        {
          "label": "Social sentiment gap, D minus R (pts)",
          "values": [
            -6.5,
            -4.5,
            -12.1,
            -9.3,
            -2.0,
            -1.4,
            -5.3,
            -18.5,
            -8.4,
            -6.5,
            -8.6,
            4.0,
            -1.6,
            -6.0,
            -3.5,
            -6.0,
            -5.9,
            -9.0,
            -8.2,
            -6.3,
            -4.5,
            -11.6,
            -11.7,
            -13.3,
            -6.1,
            -5.6,
            -4.8,
            -1.0,
            -12.5,
            -5.6,
            -4.8,
            -5.0,
            -5.4,
            -6.8,
            1.7,
            2.8,
            0.3,
            -1.5,
            1.3,
            4.4,
            2.9,
            2.9,
            -0.3,
            -13.7,
            -0.4,
            2.9,
            0.9,
            -3.0,
            2.8,
            -3.3,
            2.8,
            3.6
          ],
          "color": "var(--rc-blue)",
          "negColor": "var(--rc-red)",
          "type": "bar"
        },
        {
          "label": "Kalshi: Democratic Senate control (%)",
          "values": [
            29.0,
            29.0,
            29.0,
            33.0,
            33.0,
            32.0,
            32.0,
            32.0,
            34.0,
            34.0,
            34.0,
            35.0,
            37.0,
            39.0,
            40.0,
            41.0,
            44.0,
            51.0,
            50.0,
            47.9,
            51.3,
            50.0,
            52.0,
            48.0,
            48.0,
            43.0,
            44.0,
            46.0,
            44.0,
            43.0,
            42.0,
            43.0,
            41.0,
            40.0,
            44.0,
            45.0,
            45.0,
            46.0,
            46.0,
            48.0,
            46.0,
            48.0,
            48.0,
            49.0,
            60.0,
            62.0,
            63.0,
            61.0,
            63.0,
            63.0,
            73.0,
            76.0
          ],
          "color": "var(--rc-green)",
          "axis": "right"
        }
      ]
    },
    {
      "kind": "callout",
      "kicker": "05 · THE SEPTEMBER REPRICING",
      "title": "The best Republican day of the fall lasted two days",
      "heading": "Sep 10: a $5,000 promise, then a fade, then the market moved",
      "paragraphs": [
        "At the Republican midterm convention in Dallas on the evening of Sep 9 ET, President Trump promised a $5,000 \"dividend\" to every adult citizen if Republicans held both chambers. The clip from @RapidResponse47 drew 16.7M engagements. On Sep 10, sentiment about Republicans hit 74 on 278M engagements, the party's strongest day since the July break.",
        "By Sep 12 it was back to 51. In the week of Sep 14 the gap was -0.4, and that week Kalshi repriced Democratic Senate control from 49% (Sep 13) to 60% (Sep 19). Polls had not moved much: the generic ballot went from D+7.3 to D+7.8."
      ]
    },
    {
      "kind": "table",
      "kicker": "06 · WHAT EACH SIGNAL SAID",
      "title": "Polls, markets and social at four moments",
      "sub": "Generic ballot and Trump net approval: Silver Bulletin daily averages. Senate odds: Kalshi, last daily close of the week. Social: LunarCrush weekly party sentiment gap. Polls had Democrats ahead all year. The Senate market committed in September. Social shows when the Republican side of the conversation gave way.",
      "table": {
        "columns": [
          {
            "key": "when",
            "label": "Week of",
            "kind": "text",
            "tone": "accent"
          },
          {
            "key": "gb",
            "label": "Generic ballot",
            "kind": "text"
          },
          {
            "key": "ap",
            "label": "Trump net approval",
            "kind": "text"
          },
          {
            "key": "mk",
            "label": "Kalshi: Dem Senate",
            "kind": "text"
          },
          {
            "key": "gap",
            "label": "Social gap (D-R)",
            "kind": "gap"
          }
        ],
        "rows": [
          {
            "when": "Jan 5, 2026",
            "gb": "D+5.0",
            "ap": "-12.1",
            "mk": "34%",
            "gap": "-8.4 pts"
          },
          {
            "when": "Jul 6, 2026",
            "gb": "D+6.2",
            "ap": "-16.4",
            "mk": "44%",
            "gap": "+1.7 pts"
          },
          {
            "when": "Sep 14, 2026",
            "gb": "D+7.8",
            "ap": "-20.5",
            "mk": "60%",
            "gap": "-0.4 pts"
          },
          {
            "when": "Oct 26, 2026",
            "gb": "D+8.8",
            "ap": "-22.6",
            "mk": "73%",
            "gap": "+2.8 pts"
          }
        ]
      }
    },
    {
      "kicker": "07 · THE BACKDROP",
      "title": "A war, gas prices and the president's own numbers",
      "sub": "Monthly engagement-weighted sentiment on three topics. The Iran war began on Feb 28 and drew 12.6 billion engagements in March at a sentiment of 24, the most negative major topic of the year. Gas-price conversation spiked the same month. Sentiment about Trump eased from 60 in late 2025 to 52 in October. From Sep 1 to election day the largest issue topics by engagement were the economy (8.0B), inflation (4.2B), Epstein (3.0B) and the Iran war (2.9B).",
      "kind": "lines",
      "unit": "sentiment",
      "labels": [
        "Nov '25",
        "Dec '25",
        "Jan '26",
        "Feb '26",
        "Mar '26",
        "Apr '26",
        "May '26",
        "Jun '26",
        "Jul '26",
        "Aug '26",
        "Sep '26",
        "Oct '26"
      ],
      "series": [
        {
          "label": "Trump",
          "values": [
            59.5,
            60.2,
            58.7,
            57.2,
            54.7,
            53.5,
            58.0,
            58.7,
            57.6,
            54.0,
            55.0,
            52.2
          ],
          "color": "var(--rc-red)"
        },
        {
          "label": "Iran war",
          "values": [
            46.4,
            10.7,
            37.5,
            29.1,
            24.0,
            32.1,
            40.6,
            40.6,
            34.6,
            36.5,
            35.7,
            36.5
          ],
          "color": "var(--rc-amber)"
        },
        {
          "label": "Gas prices",
          "values": [
            53.0,
            60.8,
            67.7,
            78.0,
            54.5,
            51.8,
            53.5,
            57.6,
            62.9,
            65.5,
            48.9,
            44.1
          ],
          "color": "var(--rc-green)"
        }
      ]
    },
    {
      "kind": "table",
      "kicker": "08 · THE FINAL STRETCH",
      "title": "Democrats led every day from October 30",
      "sub": "Daily party sentiment and Democratic share of combined engagement. Republicans led on 9 of the 10 days to Oct 29. Democrats led every day from Oct 30 through election day. Nov 4 is shown for reference: it is reaction to the results, not a signal.",
      "table": {
        "columns": [
          {
            "key": "d",
            "label": "Day",
            "kind": "date"
          },
          {
            "key": "dem",
            "label": "Democrats",
            "kind": "num"
          },
          {
            "key": "rep",
            "label": "Republicans",
            "kind": "num"
          },
          {
            "key": "gap",
            "label": "Gap",
            "kind": "gap"
          },
          {
            "key": "share",
            "label": "Dem share of engagement",
            "kind": "text"
          }
        ],
        "rows": [
          {
            "d": "Oct 26",
            "dem": 49,
            "rep": 57,
            "gap": "-8 pts",
            "share": "58.2%"
          },
          {
            "d": "Oct 27",
            "dem": 52,
            "rep": 55,
            "gap": "-3 pts",
            "share": "60.4%"
          },
          {
            "d": "Oct 28",
            "dem": 55,
            "rep": 52,
            "gap": "+3 pts",
            "share": "62.4%"
          },
          {
            "d": "Oct 29",
            "dem": 50,
            "rep": 52,
            "gap": "-2 pts",
            "share": "63.2%"
          },
          {
            "d": "Oct 30",
            "dem": 61,
            "rep": 49,
            "gap": "+12 pts",
            "share": "64.3%"
          },
          {
            "d": "Oct 31",
            "dem": 59,
            "rep": 52,
            "gap": "+7 pts",
            "share": "64.7%"
          },
          {
            "d": "Nov 1",
            "dem": 59,
            "rep": 52,
            "gap": "+7 pts",
            "share": "63.5%"
          },
          {
            "d": "Nov 2",
            "dem": 64,
            "rep": 57,
            "gap": "+7 pts",
            "share": "63.9%"
          },
          {
            "d": "Nov 3 (vote)",
            "dem": 65,
            "rep": 63,
            "gap": "+2 pts",
            "share": "60.0%"
          },
          {
            "d": "Nov 4 (after results)",
            "dem": 76,
            "rep": 62,
            "gap": "+14 pts",
            "share": "65.2%"
          }
        ]
      }
    },
    {
      "kicker": "09 · ELECTION NIGHT, BY THE HOUR",
      "title": "As results came in, the split opened at 9pm Eastern",
      "sub": "Hourly party sentiment and hourly engagement on \"midterms\", noon ET Nov 3 to 2pm ET Nov 4. The parties were level through 7pm ET (66 vs 65). At 9pm the gap was 8 points, at 11pm 15, and it stayed above 10 through the next afternoon. Engagement on the two parties peaked at 11pm ET, 171M interactions in one hour.",
      "kind": "lines",
      "unit": "sentiment",
      "labels": [
        "Nov 3 12p",
        "Nov 3 1p",
        "Nov 3 2p",
        "Nov 3 3p",
        "Nov 3 4p",
        "Nov 3 5p",
        "Nov 3 6p",
        "Nov 3 7p",
        "Nov 3 8p",
        "Nov 3 9p",
        "Nov 3 10p",
        "Nov 3 11p",
        "Nov 4 12a",
        "Nov 4 1a",
        "Nov 4 2a",
        "Nov 4 3a",
        "Nov 4 4a",
        "Nov 4 5a",
        "Nov 4 6a",
        "Nov 4 7a",
        "Nov 4 8a",
        "Nov 4 9a",
        "Nov 4 10a",
        "Nov 4 11a",
        "Nov 4 12p",
        "Nov 4 1p",
        "Nov 4 2p"
      ],
      "series": [
        {
          "label": "\"Midterms\" engagement per hour (M)",
          "values": [
            21.3,
            21.7,
            23.6,
            25.5,
            26.6,
            28.6,
            30.8,
            34.8,
            38.4,
            43.0,
            48.5,
            52.9,
            56.0,
            47.6,
            43.1,
            40.5,
            36.9,
            37.9,
            41.9,
            49.3,
            50.9,
            53.7,
            53.1,
            54.7,
            56.5,
            57.6,
            48.5
          ],
          "color": "var(--rc-amber)",
          "type": "bar"
        },
        {
          "label": "Democrats",
          "values": [
            64,
            63,
            63,
            65,
            64,
            61,
            64,
            66,
            69,
            73,
            74,
            74,
            74,
            73,
            72,
            72,
            71,
            71,
            71,
            72,
            71,
            71,
            69,
            69,
            69,
            69,
            68
          ],
          "color": "var(--rc-blue)",
          "axis": "right"
        },
        {
          "label": "Republicans",
          "values": [
            62,
            63,
            62,
            62,
            63,
            61,
            63,
            65,
            66,
            65,
            61,
            59,
            58,
            59,
            58,
            59,
            59,
            58,
            58,
            58,
            57,
            57,
            55,
            56,
            58,
            56,
            57
          ],
          "color": "var(--rc-red)",
          "axis": "right"
        }
      ]
    },
    {
      "kind": "qa",
      "kicker": "10 · THE SAME SEARCH, WITH AND WITHOUT",
      "title": "Five questions about the race. Web search vs LunarCrush.",
      "sub": "On Nov 4, after the results, we asked an AI assistant with live web search five questions about how the race looked online, and let it estimate. Left: its answer, excerpted verbatim. Right: the answers from LunarCrush data, all on this page. It answered none from a measured figure, and its estimate for the year, that Democrats held the better tone for most of it, is the opposite of the data: Republicans led 39 of 52 weeks.",
      "qa": {
        "offTag": "WEB SEARCH ONLY",
        "offText": "1. Estimate: Democrats held the better tone for most of the year. I found no evidence of a flip. If one occurred, the most plausible window is mid-September. 2. Estimate: Democrats led from about Oct 20. That is the usual start of early-vote and turnout-story momentum, not a measured date. 3. I found no source on what the online conversation was doing at that moment. 4. No measured volume ranking found. Estimated ranking: economy, cost of living and tariffs; Trump and his approval; healthcare costs; immigration; democracy and redistricting. 5. No verified time. Estimate: around 9:30-10:30 pm ET. Treat this as a guess.",
        "onTag": "WITH LUNARCRUSH",
        "onText": "1. Republicans held the better tone in 33 of 34 weeks from November to June. The edge ended in the week of July 6; after that Republicans led only 6 of 17 weeks. 2. Democrats, from Oct 30 (61 vs 49), every day through Nov 3. 3. A Republican spike on Sep 10 (sentiment 74 on 278M engagements, Trump's $5,000 dividend promise) faded by Sep 12; the gap was -0.4 the week Kalshi went from 49% to 60%. 4. Economy 8.0B engagements, inflation 4.2B, Epstein 3.0B, Iran war 2.9B, tariffs 1.9B, gas prices 1.0B. 5. 9pm ET: an 8-point gap, 15 by 11pm."
      }
    },
    {
      "kind": "callout",
      "kicker": "11 · WHAT EACH SIGNAL TELLS YOU",
      "title": "Polls, prices and posts",
      "heading": "Polls estimate the vote. Markets give you a price. Social gives you the tone and the posts behind it, every hour.",
      "paragraphs": [
        "Polls: Democrats led the generic ballot all year (D+5.0 in January, D+8.8 at the end), as daily averages of surveys.",
        "Prediction markets: one price. Kalshi traded Democratic Senate control between 40% and 55% from March to early September, then went from 49% to 60% in six days (Sep 13 to Sep 19).",
        "Web search and news: narratives and individual polls. Asked how the race looked online, a web-search AI could estimate but not measure, and its estimate of the year's tone was wrong.",
        "LunarCrush: daily and hourly sentiment, engagement and creator counts for any party, person or issue across X, Reddit, TikTok, YouTube, Instagram and 10K+ news sources, with the posts behind each move. That is how this page dates the July break, explains the Sep 10 spike and times the election-night split."
      ]
    },
    {
      "kind": "callout",
      "kicker": "12 · HOW WE BUILT IT",
      "title": "Sources and method",
      "heading": "Every number traces to a pull made on Nov 4, 2026",
      "paragraphs": [
        "Social: LunarCrush topic time series (daily from Nov 5, 2025; hourly for the final week) for 24 topics, pulled 19:26 UTC Nov 4. Through election day the party topics cover 6.94M posts about Democrats and 4.35M about Republicans. Sentiment is the engagement-weighted share of positive engagement on posts about the topic, whoever posts them, so it measures the tone of the conversation about a party, not support for it. Weekly sentiment is the engagement-weighted mean of daily values; weekly averages across a period are simple means of weekly gaps. Engagement is summable; creator counts are unique and never summed.",
        "Markets: Kalshi Senate and House control contracts via the public API, daily close; Polymarket in the data download. Polls: Silver Bulletin generic-ballot and Trump-approval daily averages as published Nov 4. Lead-lag: sign of the weekly gap against the next week's change in Kalshi's Senate price, 50 weeks.",
        "The with-and-without test ran Nov 4. A second run that asked for the measured figures directly (two assistants, Claude Sonnet and Gemini 4 Argon) returned no social figures; both answers are in the AI guide download."
      ]
    },
    {
      "kind": "dataset",
      "kicker": "13 · THE DATA",
      "title": "Take the data",
      "sub": "365 days of daily social metrics on 24 political and issue topics, the weekly party panel, and the final week by the hour.",
      "downloads": [
        {
          "label": "⬇ Daily topics, 365 days (Parquet)",
          "file": "midterms-2026-social-lookback/lc_midterms_daily_topics.parquet",
          "format": "parquet",
          "encodingFormat": "application/vnd.apache.parquet",
          "primary": true
        },
        {
          "label": "⬇ Weekly party panel (CSV)",
          "file": "midterms-2026-social-lookback/lc_midterms_weekly_party.csv",
          "format": "csv",
          "encodingFormat": "text/csv"
        },
        {
          "label": "⬇ Final week by the hour (CSV)",
          "file": "midterms-2026-social-lookback/lc_midterms_hourly_final_week.csv",
          "format": "csv",
          "encodingFormat": "text/csv"
        },
        {
          "label": "⬇ AI guide (.md)",
          "file": "midterms-2026-social-lookback/lc_midterms_ai_guide.md",
          "format": "md",
          "encodingFormat": "text/markdown"
        }
      ],
      "downloadNote": "Every table also ships in the other format: swap .csv and .parquet in any file name. The hourly commercial feed covers every topic on this page and thousands more, live.",
      "methodology": "Social data pulled 19:26 UTC Nov 4, 2026. Nov 4 is a partial day."
    }
  ]
};

/* ─── Registry ───────────────────────────────────────────────────── */

export const RESEARCH_STUDIES: Study[] = [
  midterms2026Lookback,
  binanceHackReconstructed,
  ratesCrowdVsCurve,
  earningsSocialAtlas,
  attentionCascade,
  contextAdvantage,
  productSentimentAtlas,
  peopleIndex,
];

export function getStudyBySlug(slug: string): Study | undefined {
  return RESEARCH_STUDIES.find((s) => s.slug === slug);
}

export const RESEARCH_INDEX_META = {
  title: 'LunarCrush Research: Social Media Sentiment Studies',
  description:
    'Social media sentiment studies from LunarCrush: attention curves, league tables, and datasets across earnings and sectors. Free, no registration.',
  /** DEV-11563: card art for the hub itself. Falls back to study #1's artwork
   *  so the index never shares the generic site-wide OG image. */
  ogImage: RESEARCH_STUDIES[0]?.ogImage ?? '/research/og-image.png',
} as const;

/** Collect the download list from a study's dataset block (JSON-LD distributions). */
function studyDownloads(study: Study): StudyDownload[] {
  const block = study.blocks.find((b) => b.kind === 'dataset');
  return block && block.kind === 'dataset' ? block.downloads : [];
}

/**
 * Dataset + Article JSON-LD for a study.
 *
 * DEV-11563: lives here, in the pure-data module, rather than in
 * ResearchStudyPage.tsx — the build-time prerenderer
 * (scripts/generate-og-pages.ts) needs the identical graph and cannot import a
 * React component. Both the runtime page and the prerender now share this one
 * definition, so the markup a crawler sees and the markup a browser builds
 * cannot drift apart.
 */
export function buildStudyJsonLd(study: Study) {
  const studyUrl = `${CANONICAL_BASE}/research/${study.slug}`;
  // Dataset node only where a dataset exists; Article-only otherwise
  // (e.g. Context Advantage, whose downloads are packs, not a dataset).
  const dataset = study.dataset && {
    '@type': 'Dataset',
    name: study.dataset.name,
    description: study.dataset.description,
    url: studyUrl,
    creator: { '@type': 'Organization', name: 'LunarCrush', url: CANONICAL_BASE },
    ...(study.dataset.temporalCoverage && { temporalCoverage: study.dataset.temporalCoverage }),
    ...(study.dataset.variableMeasured && { variableMeasured: study.dataset.variableMeasured }),
    distribution: studyDownloads(study).map((d) => ({
      '@type': 'DataDownload',
      encodingFormat: d.encodingFormat,
      contentUrl: `${CANONICAL_BASE}/research/${d.file}`,
    })),
  };
  const article = {
    '@type': 'Article',
    headline: study.title,
    description: study.metaDescription,
    author: { '@type': 'Organization', name: 'LunarCrush' },
    publisher: { '@type': 'Organization', name: 'LunarCrush', url: CANONICAL_BASE },
    image: study.ogImage,
    datePublished: study.datePublished,
    dateModified: study.dateModified,
  };
  return { '@context': 'https://schema.org', '@graph': dataset ? [dataset, article] : [article] };
}
